import React, { useState, useRef, useEffect } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  ReferenceArea,
  Legend,
} from "recharts";
import {
  RouteIcon,
  TrendingUp,
  Users,
  Activity,
  Crosshair,
  Sparkles,
  Trophy,
  ClipboardList,
  NotebookPen,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Zap,
  Brain,
  BarChart3,
  X,
  UserCheck,
  Building2,
  HeartHandshake,
  CalendarDays,
  CalendarCheck,
  Stethoscope,
  FlaskConical,
  Headphones,
  Receipt,
  Smartphone,
  Globe,
  HeartPulse,
  MessageSquare,
  ShoppingCart,
  CircleAlert,
  Lightbulb,
  TrendingDown,
  ScanSearch,
  Filter,
  Bell,
  Send,
  PanelLeftClose,
  PanelLeft,
  Archive,
  Link as LinkIcon,
  ExternalLink,
  History,
  Heart,
  MessageCircle,
  Share2,
  Upload,
  MapPin,
  ThumbsUp,
  ThumbsDown,
  EyeOff,
  Eye,
  Phone,
  Mail,
} from "lucide-react";

import overviewMock from "../../mocks/overview.json";
import etapasMock from "../../mocks/etapas.json";
import tendenciasMock from "../../mocks/tendencias.json";
import driversMock from "../../mocks/drivers.json";
import segmentacaoMock from "../../mocks/segmentacao.json";
import benchmarkingMock from "../../mocks/benchmarking.json";

// Reassemble DATA from the 6 thematic mocks across 4 periods.
const PERIODS = ["mensal", "trimestral", "semestral", "anual"];
const DATA = Object.fromEntries(
  PERIODS.map((p) => [
    p,
    {
      ...overviewMock[p],
      ...etapasMock[p],
      ...tendenciasMock[p],
      ...driversMock[p],
      ...segmentacaoMock[p],
      ...benchmarkingMock[p],
    },
  ]),
);

// --- Logo ---------------------------------------------------------------------
const LOGO =
  "data:image/png;base64,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";

// --- Apple-like design tokens -------------------------------------------------
const C = {
  // Unimed greens
  g900: "#003E1F",
  g800: "#005229",
  g700: "#006633",
  g600: "#008040",
  g500: "#00994D",
  g400: "#00B35A",
  g300: "#4DC87E",
  g200: "#B3E6C8",
  g100: "#E5F5EC",
  g50: "#F0FAF4",
  // 121 purple
  p700: "#3D2B9E",
  p600: "#5243C2",
  p500: "#7B6CF6",
  p400: "#A99DF9",
  p100: "#EEE9FF",
  // Neutrals - Apple style
  bg: "#F5F5F7",
  surface: "#FFFFFF",
  surf2: "#FAFAFA",
  surf3: "#F5F5F7",
  border: "#E8E8ED",
  border2: "#D2D2D7",
  text: "#1D1D1F",
  text2: "#3D3D3F",
  text3: "#6E6E73",
  text4: "#AEAEB2",
  // Semantic
  red: "#FF3B30",
  redBg: "#FFF5F5",
  orange: "#FF9500",
  orangeBg: "#FFFAF0",
  yellow: "#FFCC00",
  green: "#34C759",
  greenBg: "#F0FFF4",
  blue: "#007AFF",
  blueBg: "#F0F5FF",
  purple: "#AF52DE",
  purpleBg: "#F8F0FF",
  // Chart zones
  zCrit: "#FF3B3009",
  zWarn: "#FF950009",
  zOk: "#FFCC0009",
  zGood: "#34C75909",
  white: "#FFFFFF",
};
const F = {
  display:
    "-apple-system,BlinkMacSystemFont,'SF Pro Display','Helvetica Neue',sans-serif",
  text: "-apple-system,BlinkMacSystemFont,'SF Pro Text','Helvetica Neue',sans-serif",
  mono: "'SF Mono','Monaco','Cascadia Code','Roboto Mono',monospace",
};

// --- Short axis labels --------------------------------------------------------
const SHORT = {
  Vendas: "Vendas",
  "Boas-Vindas": "Boas-Vindas",
  "Agend. Consultas": "Agend. Consultas",
  Consulta: "Consulta",
  "Agend. Exames": "Agend. Exames",
  Exames: "Exames",
  "Atend. Operadora": "Atend. Op.",
  Aplicativo: "Aplicativo",
  Portal: "Portal",
  "Promoção da Saúde": "Prom. Saúde",
  "Comunicação Operadora": "Comunicação",
};
const PER = {
  mensal: { label: "Março 2026", short: "Mar/26", ondas: 1, resp: 214 },
  trimestral: { label: "T1 2026", short: "T1/26", ondas: 3, resp: 612 },
  semestral: { label: "S2 2025", short: "S2/25", ondas: 2, resp: 1184 },
  anual: { label: "Ano 2025", short: "2025", ondas: 4, resp: 2384 },
};

// Journey cfg: CES Top2Box %, higher = better
// --- Journey stage icons (Lucide) --------------------------------------------
const JICONS = {
  Vendas: ShoppingCart,
  "Boas-Vindas": HeartHandshake,
  "Agend. Consultas": CalendarDays,
  Consulta: Stethoscope,
  "Agend. Exames": CalendarCheck,
  Exames: FlaskConical,
  "Atend. Operadora": Headphones,
  Aplicativo: Smartphone,
  Portal: Globe,
  "Promoção da Saúde": HeartPulse,
  "Comunicação Op.": MessageSquare,
};

const JCFG = {
  Vendas: { tipo: "csat", meta: 80, sys: 70, label: "CSAT" },
  "Boas-Vindas": { tipo: "csat", meta: 82, sys: 72, label: "CSAT" },
  "Agend. Consultas": { tipo: "ces", meta: 65, sys: 48, label: "CES" },
  Consulta: { tipo: "csat", meta: 85, sys: 76, label: "CSAT" },
  "Agend. Exames": { tipo: "ces", meta: 62, sys: 46, label: "CES" },
  Exames: { tipo: "csat", meta: 72, sys: 65, label: "CSAT" },
  "Atend. Operadora": { tipo: "ces", meta: 65, sys: 42, label: "CES" },
  Aplicativo: { tipo: "ces", meta: 72, sys: 55, label: "CES" },
  Portal: { tipo: "ces", meta: 68, sys: 50, label: "CES" },
  "Promoção da Saúde": { tipo: "csat", meta: 78, sys: 68, label: "CSAT" },
  "Comunicação Op.": { tipo: "csat", meta: 75, sys: 62, label: "CSAT" },
};
const META_NPS = 70,
  SYS_NPS = 52,
  UNIMED = "Unimed";

// --- Cidades da área de cobertura (RM Campinas) -------------------------------
// Lista global usada pelo filtro de cidade no header. Em produção viria da API
// (lista de municípios cobertos por cada cliente Unimed).
const CIDADES_COBERTURA = [
  "Campinas",
  "Sumaré",
  "Hortolândia",
  "Valinhos",
  "Vinhedo",
  "Paulínia",
  "Jaguariúna",
  "Itatiba",
];

// --- Última visita do usuário ao dashboard ----------------------------------
// Em produção viria da API (último login do usuário). Aqui mockamos pra demo.
const ULTIMA_VISITA = "18 de março";

// O que mudou desde a última visita do usuário — alimentado por diff snapshot.
// Em produção: comparar valor atual vs valor no momento da última visita.
const MUDANCAS_DESDE_ULTIMA_VISITA = [
  { etapa: "Atend. Operadora", delta: -3, direcao: "piorou" },
  { etapa: "Agend. Exames", delta: -1, direcao: "estavel" },
  { etapa: "Boas-Vindas", delta: +2, direcao: "melhorou" },
];

// --- Verbatins por etapa (mock — em produção vêm das respostas abertas) -----
// Cada verbatim é um trecho real do beneficiário, classificado pelo NLP do Eva
// Engine na etapa de origem da pesquisa, com sentimento e driver associado.
const VERBATINS_POR_ETAPA = {
  Vendas: [
    // Reclamações — uma por driver
    {
      texto:
        "Falei com 3 corretores diferentes e cada um disse uma coisa sobre carências. Saí sem entender.",
      genero: "homem",
      idade: 51,
      cidade: "Sumaré",
      data: "6/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Tive que correr atrás de tudo, ninguém me ajudou. Pra cada dúvida, uma ligação nova.",
      genero: "homem",
      idade: 44,
      cidade: "Hortolândia",
      data: "4/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "O corretor parecia mais interessado na comissão do que em entender o que eu precisava.",
      genero: "mulher",
      idade: 62,
      cidade: "Sumaré",
      data: "2/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Vendi como cobertura completa, fui descobrir depois que metade das coisas tem coparticipação.",
      genero: "homem",
      idade: 52,
      cidade: "Paulínia",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Conciliação (Concordância)",
    },
    // Elogios — um por driver
    {
      texto:
        "Recebi tudo por escrito antes de assinar — carências, rede, valores. Não tive nenhuma surpresa depois.",
      genero: "mulher",
      idade: 43,
      cidade: "Campinas",
      data: "18/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "O corretor me ligou várias vezes pra tirar dúvidas e até depois da venda continuou disponível.",
      genero: "mulher",
      idade: 39,
      cidade: "Vinhedo",
      data: "22/Mar",
      tipo: "promotor",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "O corretor explicou tudo direitinho e ainda me ajudou a comparar planos. Saí sabendo o que estava contratando.",
      genero: "mulher",
      idade: 38,
      cidade: "Campinas",
      data: "14/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "O preço pareceu justo pra cobertura. Não me senti enganado em nenhum momento.",
      genero: "mulher",
      idade: 42,
      cidade: "Valinhos",
      data: "10/Mar",
      tipo: "promotor",
      driver: "Conciliação (Concordância)",
    },
    {
      texto:
        "Quando perguntei sobre carências da gestação, a corretora foi vaga. Tive que ler o contrato sozinha pra entender.",
      genero: "mulher",
      idade: 32,
      cidade: "Jaguariúna",
      data: "19/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "O corretor ligava só pra fechar a venda. Depois de assinar, sumiu. Tive que ligar pra central pra qualquer coisa.",
      genero: "homem",
      idade: 48,
      cidade: "Hortolândia",
      data: "11/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "O corretor me passou o contrato no WhatsApp já preenchido, pediu só pra eu assinar. Senti que não foi cuidadoso.",
      genero: "mulher",
      idade: 54,
      cidade: "Sumaré",
      data: "7/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Recebi material em PDF detalhando rede e carências. Sem letra miúda, sem surpresa. Bem profissional.",
      genero: "homem",
      idade: 36,
      cidade: "Itatiba",
      data: "21/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Comparei com 3 outras operadoras antes de fechar. A Unimed entregou o que prometeu, sem pegadinha.",
      genero: "mulher",
      idade: 41,
      cidade: "Campinas",
      data: "17/Mar",
      tipo: "promotor",
      driver: "Conciliação (Concordância)",
    },
  ],
  "Boas-Vindas": [
    {
      texto:
        "Não recebi nenhum tipo de boas-vindas. Tive que correr atrás de tudo sozinho nos primeiros meses.",
      genero: "homem",
      idade: 52,
      cidade: "Paulínia",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Tentei baixar o app nos primeiros dias e não consegui me cadastrar. Ninguém soube me ajudar pelo telefone.",
      genero: "homem",
      idade: 35,
      cidade: "Vinhedo",
      data: "5/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "Liguei pra entender meu plano e a atendente foi seca, parecia incomodada com minhas perguntas.",
      genero: "mulher",
      idade: 46,
      cidade: "Hortolândia",
      data: "11/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Ninguém me ofereceu ajuda. Tive que adivinhar como funcionava a coparticipação, a rede, tudo.",
      genero: "mulher",
      idade: 49,
      cidade: "Hortolândia",
      data: "3/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Recebi e-mail, WhatsApp e até uma cartinha em casa. Adorei a recepção, me senti acolhida desde o início.",
      genero: "mulher",
      idade: 33,
      cidade: "Valinhos",
      data: "24/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Recebi o cartão por e-mail no mesmo dia, com instruções de como baixar o app. Achei super organizado.",
      genero: "homem",
      idade: 34,
      cidade: "Vinhedo",
      data: "22/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "Quando liguei pra perguntar onde achar minha documentação, a moça foi um anjo, me explicou tudo com calma.",
      genero: "mulher",
      idade: 43,
      cidade: "Vinhedo",
      data: "19/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Me ligaram pra fazer um onboarding por telefone, perguntando se tinha dúvidas. Achei o cuidado incrível.",
      genero: "homem",
      idade: 59,
      cidade: "Campinas",
      data: "14/Mar",
      tipo: "promotor",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Demorei uma semana pra conseguir ativar o login no app. O suporte me passou 4 senhas diferentes.",
      genero: "homem",
      idade: 28,
      cidade: "Campinas",
      data: "13/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "No primeiro mês precisei de uma autorização e ninguém me explicou nada sobre como funcionava o processo.",
      genero: "mulher",
      idade: 47,
      cidade: "Itatiba",
      data: "9/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Quando liguei pela primeira vez, a atendente foi extremamente impaciente comigo. Quase cancelei.",
      genero: "mulher",
      idade: 35,
      cidade: "Campinas",
      data: "6/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Recebi um vídeo curto explicando como usar a rede, como pedir reembolso, tudo. Adorei a iniciativa.",
      genero: "homem",
      idade: 52,
      cidade: "Paulínia",
      data: "20/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Em 10 minutos eu já tinha o app instalado, carteirinha digital e meu primeiro check-in agendado. Impecável.",
      genero: "mulher",
      idade: 31,
      cidade: "Vinhedo",
      data: "16/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
  ],
  "Agend. Consultas": [
    {
      texto:
        "Tentei agendar com o cardiologista e a primeira data disponível era pra daqui 4 meses. Fui pagar particular.",
      genero: "homem",
      idade: 58,
      cidade: "Sumaré",
      data: "11/Mar",
      tipo: "detrator",
      driver: "Dificuldade p/ agendar",
    },
    {
      texto:
        "Esperei 22 minutos na linha pra confirmar uma simples consulta de rotina. Absurdo.",
      genero: "homem",
      idade: 48,
      cidade: "Sumaré",
      data: "7/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "O app trava na hora de confirmar. Acabo ligando porque é mais rápido que ficar tentando.",
      genero: "mulher",
      idade: 36,
      cidade: "Itatiba",
      data: "7/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "A central de marcação é simpática mas eles não sabem nem me dizer qual médico atende minha condição.",
      genero: "mulher",
      idade: 49,
      cidade: "Hortolândia",
      data: "3/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Marquei pediatra pra criança em 2 dias. Sempre tem horário disponível, nunca passei aperto.",
      genero: "mulher",
      idade: 33,
      cidade: "Valinhos",
      data: "23/Mar",
      tipo: "promotor",
      driver: "Dificuldade p/ agendar",
    },
    {
      texto:
        "Liguei e em 4 minutos já tinha minha consulta marcada com o ortopedista. Atendimento eficiente.",
      genero: "mulher",
      idade: 31,
      cidade: "Valinhos",
      data: "20/Mar",
      tipo: "promotor",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Consegui marcar pelo app em 2 minutos. Achei o melhor sistema de agendamento que já usei.",
      genero: "mulher",
      idade: 29,
      cidade: "Campinas",
      data: "20/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "Falei com a central, expliquei o sintoma e a moça já me orientou pro especialista certo. Resolveu na hora.",
      genero: "homem",
      idade: 60,
      cidade: "Paulínia",
      data: "15/Mar",
      tipo: "promotor",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Tentei agendar dermatologista 5 vezes em diferentes meses. Sempre lotado. Acabei desistindo do plano.",
      genero: "mulher",
      idade: 44,
      cidade: "Hortolândia",
      data: "14/Mar",
      tipo: "detrator",
      driver: "Dificuldade p/ agendar",
    },
    {
      texto:
        "Liguei e fiquei 35 minutos esperando. Atendente me transferiu sem aviso e a chamada caiu.",
      genero: "homem",
      idade: 39,
      cidade: "Campinas",
      data: "10/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Pedi pra remarcar e a atendente disse que eu tinha que ligar de novo no setor de cancelamento. Ridículo.",
      genero: "mulher",
      idade: 26,
      cidade: "Sumaré",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "App lista os médicos com avaliação de outros pacientes. Achei super útil pra escolher quem ia me atender.",
      genero: "homem",
      idade: 33,
      cidade: "Vinhedo",
      data: "22/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "Liguei sem saber qual especialista procurar e a atendente foi me ajudando, perguntando os sintomas. Resolvido.",
      genero: "mulher",
      idade: 56,
      cidade: "Paulínia",
      data: "18/Mar",
      tipo: "promotor",
      driver: "Resolutividade no 1º contato",
    },
  ],
  Consulta: [
    {
      texto:
        "O médico mal olhou pra mim. Receitou e me dispensou sem pedir nem um exame complementar.",
      genero: "mulher",
      idade: 62,
      cidade: "Sumaré",
      data: "5/Mar",
      tipo: "detrator",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "O médico foi frio, não me deixou fazer perguntas. Saí me sentindo um número.",
      genero: "mulher",
      idade: 50,
      cidade: "Itatiba",
      data: "9/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Tive que voltar 3 vezes no mesmo médico porque ele não conseguiu identificar o problema. Acabei procurando outro.",
      genero: "homem",
      idade: 61,
      cidade: "Sumaré",
      data: "6/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "O médico me dispensou sem nem comentar que eu deveria voltar pra acompanhar o caso.",
      genero: "mulher",
      idade: 64,
      cidade: "Hortolândia",
      data: "4/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Atendimento médico excelente. Saí com o diagnóstico claro e tudo agendado pros próximos passos.",
      genero: "homem",
      idade: 55,
      cidade: "Campinas",
      data: "12/Mar",
      tipo: "promotor",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "O médico me deu atenção, ouviu minhas preocupações, não foi aquele atendimento de 5 minutos.",
      genero: "mulher",
      idade: 43,
      cidade: "Vinhedo",
      data: "19/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Em uma consulta o médico fechou o diagnóstico, pediu os exames certos e já me orientou o tratamento. Resolveu de cara.",
      genero: "homem",
      idade: 41,
      cidade: "Paulínia",
      data: "16/Mar",
      tipo: "promotor",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "O médico já me agendou retorno e a enfermeira me ligou na semana seguinte pra ver como eu estava.",
      genero: "mulher",
      idade: 28,
      cidade: "Campinas",
      data: "22/Mar",
      tipo: "promotor",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "O médico ficou olhando o computador a consulta inteira. Não me examinou direito, não mediu pressão, nada.",
      genero: "mulher",
      idade: 38,
      cidade: "Itatiba",
      data: "13/Mar",
      tipo: "detrator",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "O médico cortava minhas falas, parecia querer me apressar. Saí com mais dúvidas do que entrei.",
      genero: "homem",
      idade: 45,
      cidade: "Campinas",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Pedi orientação sobre prevenção e o médico disse que era 'só comer bem'. Esperava mais de uma consulta médica.",
      genero: "mulher",
      idade: 29,
      cidade: "Sumaré",
      data: "5/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Médica fez anamnese completa, pediu exames específicos pra investigar. Profissional do nível de hospital particular.",
      genero: "homem",
      idade: 51,
      cidade: "Hortolândia",
      data: "21/Mar",
      tipo: "promotor",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "A médica me deu o tempo que precisei. Não me senti um número, me senti pessoa. Ela perguntou até do meu trabalho.",
      genero: "mulher",
      idade: 40,
      cidade: "Vinhedo",
      data: "17/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
  ],
  "Agend. Exames": [
    {
      texto:
        "Pra cada exame eles pedem uma autorização nova. Por que não pode ser tudo de uma vez?",
      genero: "homem",
      idade: 48,
      cidade: "Sumaré",
      data: "4/Mar",
      tipo: "detrator",
      driver: "Dificuldade p/ agendar",
    },
    {
      texto:
        "Fiquei 9 dias esperando autorização pra uma ressonância urgente. Quando saiu, tive que esperar mais 15 dias pelo exame.",
      genero: "homem",
      idade: 54,
      cidade: "Campinas",
      data: "6/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "O app não deixa eu fazer upload do pedido médico. Tenho que ir presencialmente pra protocolar.",
      genero: "mulher",
      idade: 39,
      cidade: "Jaguariúna",
      data: "10/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "Pedi o exame e fiquei sem saber se foi autorizado. Tive que ligar pra descobrir, ninguém me avisou.",
      genero: "mulher",
      idade: 47,
      cidade: "Sumaré",
      data: "9/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Consegui marcar todos os 4 exames pro mesmo dia, no mesmo lugar. Resolvido sem stress.",
      genero: "mulher",
      idade: 56,
      cidade: "Itatiba",
      data: "24/Mar",
      tipo: "promotor",
      driver: "Dificuldade p/ agendar",
    },
    {
      texto:
        "Pedido entrou na segunda, autorização saiu na terça, fiz o exame na quinta. Padrão alto.",
      genero: "mulher",
      idade: 31,
      cidade: "Valinhos",
      data: "23/Mar",
      tipo: "promotor",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Anexei o pedido pelo app, em 5 minutos tava tudo encaminhado. Adorei a praticidade.",
      genero: "homem",
      idade: 41,
      cidade: "Paulínia",
      data: "21/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "Pedi autorização pra um exame e me ligaram avisando do agendamento no mesmo dia. Surpresa boa.",
      genero: "mulher",
      idade: 47,
      cidade: "Paulínia",
      data: "21/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Pra fazer ressonância tive que ir ao laboratório, depois autorizar, depois remarcar. 4 etapas pra um exame.",
      genero: "mulher",
      idade: 49,
      cidade: "Hortolândia",
      data: "12/Mar",
      tipo: "detrator",
      driver: "Dificuldade p/ agendar",
    },
    {
      texto:
        "Autorização caiu sem eu saber. Fui ao laboratório no dia e descobri lá. Perdi o dia inteiro de trabalho.",
      genero: "homem",
      idade: 57,
      cidade: "Sumaré",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "23 dias entre pedido e exame. Pra um problema que tava me deixando preocupada todo dia.",
      genero: "mulher",
      idade: 33,
      cidade: "Campinas",
      data: "5/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Um único contato, agendaram 3 exames pro mesmo dia, mesmo lab. Sem dor de cabeça.",
      genero: "homem",
      idade: 42,
      cidade: "Valinhos",
      data: "20/Mar",
      tipo: "promotor",
      driver: "Dificuldade p/ agendar",
    },
    {
      texto:
        "Recebi SMS, e-mail e WhatsApp confirmando cada etapa. Não fiquei na dúvida em momento nenhum.",
      genero: "mulher",
      idade: 36,
      cidade: "Vinhedo",
      data: "16/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
  ],
  Exames: [
    {
      texto:
        "Cheguei no horário marcado e esperei 1h40 sentada. Nenhuma satisfação dada pela equipe.",
      genero: "mulher",
      idade: 50,
      cidade: "Itatiba",
      data: "9/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "O técnico que fez o ultrassom não conhecia bem o aparelho. Tive que repetir em outro lugar.",
      genero: "homem",
      idade: 44,
      cidade: "Hortolândia",
      data: "7/Mar",
      tipo: "detrator",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "O resultado demorou 8 dias e ninguém me avisou que estava pronto. Tive que ficar checando.",
      genero: "mulher",
      idade: 47,
      cidade: "Sumaré",
      data: "5/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Recepcionista do lab foi grossa quando eu perguntei sobre o jejum. Saí estressada antes do exame.",
      genero: "mulher",
      idade: 49,
      cidade: "Hortolândia",
      data: "3/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Exame rapidíssimo, atendimento atencioso, recebi o resultado no app em 24h. Padrão alto.",
      genero: "mulher",
      idade: 31,
      cidade: "Valinhos",
      data: "23/Mar",
      tipo: "promotor",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "O técnico explicou tudo durante o exame, me deixou tranquila. Profissionalismo nível alto.",
      genero: "mulher",
      idade: 29,
      cidade: "Campinas",
      data: "21/Mar",
      tipo: "promotor",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "Me avisaram por SMS quando o resultado saiu e me explicaram como ler. Cuidado total.",
      genero: "homem",
      idade: 60,
      cidade: "Paulínia",
      data: "19/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Estavam todos sorridentes, me ofereceram água, perguntaram se eu queria música no exame. Excelente.",
      genero: "mulher",
      idade: 33,
      cidade: "Valinhos",
      data: "24/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Marcado pras 8h, atenderam às 9h30. Sem nenhuma explicação ou pedido de desculpa.",
      genero: "mulher",
      idade: 52,
      cidade: "Itatiba",
      data: "11/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Tive que repetir o exame de sangue porque o resultado veio 'inconclusivo'. Ninguém me explicou o porquê.",
      genero: "homem",
      idade: 60,
      cidade: "Sumaré",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "Pediram pra eu trocar de roupa numa sala compartilhada, sem privacidade. Me senti péssima.",
      genero: "mulher",
      idade: 27,
      cidade: "Campinas",
      data: "4/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Cheguei, fui chamado em 5 minutos, fiz o exame, fui embora. Resultado no app no dia seguinte.",
      genero: "homem",
      idade: 48,
      cidade: "Paulínia",
      data: "22/Mar",
      tipo: "promotor",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "A enfermeira foi atenciosa, me explicou cada passo do exame. Saí menos ansiosa do que entrei.",
      genero: "mulher",
      idade: 45,
      cidade: "Vinhedo",
      data: "17/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
  ],
  "Atend. Operadora": [
    {
      texto:
        "Liguei 3 vezes e ninguém me retornou sobre a autorização. Tive que ir pessoalmente na unidade pra resolver.",
      genero: "mulher",
      idade: 45,
      cidade: "Campinas",
      data: "22/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Esperei 28 minutos no chat só pra descobrir que não era o setor certo. Me passaram pra outra fila com mais 15 min.",
      genero: "mulher",
      idade: 38,
      cidade: "Hortolândia",
      data: "15/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Atendente falou rápido demais, parecia querer se livrar de mim. Quando perguntei algo de novo, suspirou audivelmente.",
      genero: "homem",
      idade: 61,
      cidade: "Sumaré",
      data: "11/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Liguei do PA pra confirmar se estava coberto e ninguém atendeu. Acabei pagando do bolso e brigando depois.",
      genero: "homem",
      idade: 54,
      cidade: "Campinas",
      data: "6/Mar",
      tipo: "detrator",
      driver: "Atend. urgência e emergência",
    },
    {
      texto:
        "Liguei com um problema de boleto e em 4 minutos a moça já tinha enviado a 2ª via. Adorei a agilidade.",
      genero: "mulher",
      idade: 42,
      cidade: "Valinhos",
      data: "24/Mar",
      tipo: "promotor",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Atendimento foi rapidíssimo. Em 3 minutos no WhatsApp consegui o que precisava sem complicação.",
      genero: "homem",
      idade: 34,
      cidade: "Vinhedo",
      data: "22/Mar",
      tipo: "promotor",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "A moça da central foi um amor. Notou que eu estava nervosa e me acalmou antes de explicar a solução.",
      genero: "mulher",
      idade: 38,
      cidade: "Campinas",
      data: "19/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Liguei do hospital às 3 da manhã, atenderam em 30 segundos e autorizaram tudo na hora. Salvou.",
      genero: "homem",
      idade: 55,
      cidade: "Campinas",
      data: "17/Mar",
      tipo: "promotor",
      driver: "Atend. urgência e emergência",
    },
    {
      texto:
        "Liguei 4 vezes em 3 dias pra resolver uma única autorização. Cada um pedia pra eu repetir tudo do zero.",
      genero: "mulher",
      idade: 39,
      cidade: "Sumaré",
      data: "13/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Chat do WhatsApp respondeu 'aguarde' por 40 minutos. Quando voltou, sessão tinha expirado.",
      genero: "homem",
      idade: 46,
      cidade: "Hortolândia",
      data: "10/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Tentei explicar minha situação 3 vezes e a atendente me interrompeu repetindo o script. Nem ouviu meu problema.",
      genero: "mulher",
      idade: 53,
      cidade: "Campinas",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Resolveram tudo em uma única ligação de 6 minutos. A moça já tinha o histórico do meu caso na tela.",
      genero: "homem",
      idade: 37,
      cidade: "Valinhos",
      data: "21/Mar",
      tipo: "promotor",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "A atendente foi paciente, me deixou desabafar. Depois resolveu o problema. Senti que importei.",
      genero: "mulher",
      idade: 41,
      cidade: "Itatiba",
      data: "18/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
  ],
  Aplicativo: [
    {
      texto:
        "O app trava toda vez que tento ver minhas guias. Já desinstalei e instalei 3 vezes.",
      genero: "homem",
      idade: 35,
      cidade: "Vinhedo",
      data: "18/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "O app diz uma coisa, a central diz outra. Não confio mais no que aparece lá.",
      genero: "mulher",
      idade: 28,
      cidade: "Campinas",
      data: "12/Mar",
      tipo: "detrator",
      driver: "Conciliação (Concordância)",
    },
    {
      texto:
        "Mando uma dúvida pelo chat do app e demora 2 dias pra responder. Mais rápido ligar mesmo.",
      genero: "mulher",
      idade: 46,
      cidade: "Hortolândia",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "O app não resolve quase nada — tudo precisa ser confirmado por telefone depois.",
      genero: "homem",
      idade: 51,
      cidade: "Sumaré",
      data: "5/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Pelo app eu consigo marcar consulta, ver resultado de exame, baixar carteirinha. Não preciso mais ligar pra nada.",
      genero: "homem",
      idade: 41,
      cidade: "Paulínia",
      data: "23/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "O que o app mostra bate certinho com o que tenho contratado. Confiável e transparente.",
      genero: "mulher",
      idade: 56,
      cidade: "Itatiba",
      data: "20/Mar",
      tipo: "promotor",
      driver: "Conciliação (Concordância)",
    },
    {
      texto:
        "Mando pelo chat e em 5 minutos a IA já me dá uma resposta. Quando preciso de humano, vem rápido também.",
      genero: "mulher",
      idade: 29,
      cidade: "Campinas",
      data: "18/Mar",
      tipo: "promotor",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Resolvi um problema de cobrança pelo chat do app sem precisar de ligação. Bastou enviar um print do extrato.",
      genero: "mulher",
      idade: 39,
      cidade: "Vinhedo",
      data: "15/Mar",
      tipo: "promotor",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "App pede atualização toda semana. Cada uma demora 5 minutos. Já desisti de algumas coisas só por isso.",
      genero: "homem",
      idade: 34,
      cidade: "Campinas",
      data: "14/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "Vi uma cobrança no app que não tinha no extrato. Quando perguntei, era 'erro de sincronização'. Não dá pra confiar.",
      genero: "mulher",
      idade: 28,
      cidade: "Vinhedo",
      data: "11/Mar",
      tipo: "detrator",
      driver: "Conciliação (Concordância)",
    },
    {
      texto:
        "Tentei abrir um chamado pelo app e o sistema me empurrou pra ligar. Pra que tem chat então?",
      genero: "mulher",
      idade: 51,
      cidade: "Sumaré",
      data: "7/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Login com biometria, navegação rápida, tudo intuitivo. Melhor app de operadora que já usei.",
      genero: "homem",
      idade: 30,
      cidade: "Hortolândia",
      data: "20/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "Toda cobrança vem detalhada no app, com discriminação do que é coparticipação. Transparente.",
      genero: "mulher",
      idade: 44,
      cidade: "Paulínia",
      data: "16/Mar",
      tipo: "promotor",
      driver: "Conciliação (Concordância)",
    },
  ],
  Portal: [
    {
      texto:
        "O portal tem um design dos anos 2000, achar qualquer coisa é uma odisseia.",
      genero: "mulher",
      idade: 28,
      cidade: "Campinas",
      data: "11/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "Consultei o extrato no portal e tinha uma cobrança que eu não reconheci. Tive que abrir chamado pra entender.",
      genero: "mulher",
      idade: 47,
      cidade: "Sumaré",
      data: "9/Mar",
      tipo: "detrator",
      driver: "Conciliação (Concordância)",
    },
    {
      texto:
        "Não recebo nenhum aviso quando há novidades — só descubro entrando no portal de vez em quando.",
      genero: "mulher",
      idade: 64,
      cidade: "Hortolândia",
      data: "7/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "O portal carrega na velocidade de uma tartaruga. Toda vez é assim, de manhã ou à noite.",
      genero: "homem",
      idade: 52,
      cidade: "Paulínia",
      data: "5/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Portal funciona bem no computador, dá pra baixar boletos e ver extratos sem dificuldade.",
      genero: "mulher",
      idade: 56,
      cidade: "Itatiba",
      data: "17/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "Tudo bate com a fatura: extrato, coparticipação, recibo. Transparente.",
      genero: "homem",
      idade: 60,
      cidade: "Paulínia",
      data: "15/Mar",
      tipo: "promotor",
      driver: "Conciliação (Concordância)",
    },
    {
      texto:
        "O portal me notifica quando tem documento novo ou alguma pendência. Útil pra não perder prazo.",
      genero: "homem",
      idade: 59,
      cidade: "Campinas",
      data: "13/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Carrega rápido, encontro o que preciso em 2 cliques. Bem feito pro padrão de operadora.",
      genero: "mulher",
      idade: 29,
      cidade: "Campinas",
      data: "21/Mar",
      tipo: "promotor",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "Pra logar no portal são 3 senhas diferentes: uma do CPF, uma do email, uma do app. Já desisti de usar.",
      genero: "homem",
      idade: 56,
      cidade: "Itatiba",
      data: "12/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "Pedi extrato detalhado e o portal mostrou só os totais. Tive que ligar pra entender o que era cada cobrança.",
      genero: "mulher",
      idade: 49,
      cidade: "Hortolândia",
      data: "8/Mar",
      tipo: "detrator",
      driver: "Conciliação (Concordância)",
    },
    {
      texto:
        "O portal congela do nada. Tenho que reiniciar a página toda vez. Pessoal mais velho não consegue usar.",
      genero: "homem",
      idade: 62,
      cidade: "Sumaré",
      data: "5/Mar",
      tipo: "detrator",
      driver: "Agilidade no atendimento",
    },
    {
      texto:
        "No portal eu vejo todo o histórico do meu plano em um lugar só. Mais completo até que o app.",
      genero: "mulher",
      idade: 38,
      cidade: "Campinas",
      data: "19/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "O portal me avisa quando tem novidade no plano: mudança de rede, ajuste de preço, tudo. Bem útil.",
      genero: "homem",
      idade: 55,
      cidade: "Valinhos",
      data: "15/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
  ],
  "Promoção da Saúde": [
    {
      texto:
        "Nunca me ofereceram nenhum programa. Só descobri que existem porque vi no app por acaso.",
      genero: "mulher",
      idade: 64,
      cidade: "Hortolândia",
      data: "5/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Fiz a consulta de prevenção e a médica foi mecânica, parecia checklist. Não me senti vista.",
      genero: "mulher",
      idade: 62,
      cidade: "Sumaré",
      data: "4/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Não recebo informações sobre os programas disponíveis. Tudo é por conta própria.",
      genero: "homem",
      idade: 51,
      cidade: "Sumaré",
      data: "7/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "A nutricionista me passou uma dieta genérica, sem nem perguntar do meu dia a dia.",
      genero: "mulher",
      idade: 47,
      cidade: "Sumaré",
      data: "3/Mar",
      tipo: "detrator",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "Adoro o programa de gestantes. Tenho acompanhamento de nutricionista e psicóloga inclusos.",
      genero: "mulher",
      idade: 33,
      cidade: "Valinhos",
      data: "24/Mar",
      tipo: "promotor",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Faço o programa de prevenção cardio há 2 anos. Mudou minha relação com a saúde — me sinto cuidada de verdade.",
      genero: "mulher",
      idade: 43,
      cidade: "Vinhedo",
      data: "22/Mar",
      tipo: "promotor",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Recebo dicas semanais sobre saúde no WhatsApp. Conteúdo de qualidade, sem ser invasivo.",
      genero: "mulher",
      idade: 42,
      cidade: "Valinhos",
      data: "19/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "A médica do programa de prevenção foi atenciosa, fez exame completo e me orientou um plano personalizado.",
      genero: "mulher",
      idade: 38,
      cidade: "Campinas",
      data: "16/Mar",
      tipo: "promotor",
      driver: "Qualidade médica em consultas",
    },
    {
      texto:
        "Sou diabética há 10 anos e nunca recebi convite pra programa de prevenção. Descobri que existia por uma amiga.",
      genero: "mulher",
      idade: 58,
      cidade: "Hortolândia",
      data: "12/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Recebo dicas no WhatsApp mas é tudo genérico, parece copiado da internet. Esperava algo personalizado.",
      genero: "homem",
      idade: 45,
      cidade: "Sumaré",
      data: "9/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "A coach do programa parecia um robô. Falava as mesmas frases pra todo mundo, sem personalizar nada.",
      genero: "mulher",
      idade: 36,
      cidade: "Campinas",
      data: "6/Mar",
      tipo: "detrator",
      driver: "Humanização no atendimento",
    },
    {
      texto:
        "Me ligaram porque viram nos exames que eu tava no limite do colesterol. Hoje faço acompanhamento mensal.",
      genero: "homem",
      idade: 50,
      cidade: "Vinhedo",
      data: "21/Mar",
      tipo: "promotor",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "A nutricionista do programa fez plano olhando minha rotina, meu trabalho, minhas viagens. Personalizado de verdade.",
      genero: "mulher",
      idade: 42,
      cidade: "Paulínia",
      data: "17/Mar",
      tipo: "promotor",
      driver: "Qualidade médica em consultas",
    },
  ],
  "Comunicação Op.": [
    {
      texto:
        "Mandam mensagem demais. Toda semana é alguma coisa que não me interessa. Acabei silenciando.",
      genero: "mulher",
      idade: 32,
      cidade: "Campinas",
      data: "13/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Mudou minha rede credenciada e ninguém me avisou. Descobri quando precisei do médico e ele já não atendia mais.",
      genero: "homem",
      idade: 58,
      cidade: "Sumaré",
      data: "10/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Mandei e-mail perguntando sobre uma cobrança. Demoraram 8 dias e a resposta foi padrão, não resolveu nada.",
      genero: "homem",
      idade: 52,
      cidade: "Paulínia",
      data: "7/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Os comunicados pelo app aparecem todos misturados, não consigo achar o que é importante.",
      genero: "mulher",
      idade: 28,
      cidade: "Campinas",
      data: "4/Mar",
      tipo: "detrator",
      driver: "App facilitador",
    },
    {
      texto:
        "Recebo as comunicações por e-mail e WhatsApp, sempre claras. Quando teve mudança de rede, me avisaram com 30 dias.",
      genero: "homem",
      idade: 60,
      cidade: "Paulínia",
      data: "20/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Me avisaram que meu pediatra ia sair da rede com 45 dias de antecedência e ainda me sugeriram alternativas.",
      genero: "mulher",
      idade: 39,
      cidade: "Vinhedo",
      data: "18/Mar",
      tipo: "promotor",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Mandei mensagem sobre uma dúvida no plano e a resposta veio em 2 horas com a solução completa.",
      genero: "homem",
      idade: 34,
      cidade: "Vinhedo",
      data: "15/Mar",
      tipo: "promotor",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Pelo app consigo ver todos os comunicados organizados por data. Bem prático pra acompanhar.",
      genero: "homem",
      idade: 41,
      cidade: "Paulínia",
      data: "12/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
    {
      texto:
        "Aumentaram a coparticipação e só descobri quando vi o boleto. Avisar antes seria o mínimo.",
      genero: "mulher",
      idade: 47,
      cidade: "Sumaré",
      data: "14/Mar",
      tipo: "detrator",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Meu plano mudou de regulamentação e ninguém me avisou. Descobri lendo o site da ANS.",
      genero: "homem",
      idade: 53,
      cidade: "Hortolândia",
      data: "10/Mar",
      tipo: "detrator",
      driver: "Proatividade da equipe",
    },
    {
      texto:
        "Mandei e-mail 3 vezes pedindo a 2ª via do contrato. Nenhuma resposta. Tive que ir presencialmente.",
      genero: "mulher",
      idade: 31,
      cidade: "Campinas",
      data: "6/Mar",
      tipo: "detrator",
      driver: "Resolutividade no 1º contato",
    },
    {
      texto:
        "Recebi aviso de mudança de carências com 60 dias de antecedência, com explicação detalhada do impacto. Educativo.",
      genero: "homem",
      idade: 39,
      cidade: "Valinhos",
      data: "20/Mar",
      tipo: "promotor",
      driver: "Comunicação proativa",
    },
    {
      texto:
        "Os comunicados ficam todos arquivados no app, dá pra consultar a qualquer momento. Bem organizado.",
      genero: "mulher",
      idade: 44,
      cidade: "Itatiba",
      data: "17/Mar",
      tipo: "promotor",
      driver: "App facilitador",
    },
  ],
};

// --- Histórico de planos de ação por etapa ---------------------------------
// Em produção: query nos planos concluídos ligados à etapa, com cálculo de
// delta da métrica entre baseline (criação) e medição atual.
const HIST_ACOES_POR_ETAPA = {
  "Atend. Operadora": [
    {
      titulo: "Treinamento de empatia para atendentes nível 1",
      criadaEm: "out/25",
      concluidaEm: "dez/25",
      impacto: "+5pp em Humanização",
      sucesso: true,
    },
    {
      titulo: "Reduzir transferências entre setores",
      criadaEm: "jul/25",
      concluidaEm: "set/25",
      impacto: "sem efeito mensurável",
      sucesso: false,
    },
    {
      titulo: "Implementar callback automático em fila >10min",
      criadaEm: "abr/25",
      concluidaEm: "jun/25",
      impacto: "+8pp em Agilidade",
      sucesso: true,
    },
  ],
  "Agend. Exames": [
    {
      titulo: "Pré-aprovação automática para exames de rotina",
      criadaEm: "set/25",
      concluidaEm: "nov/25",
      impacto: "+12pp em Agilidade",
      sucesso: true,
    },
  ],
  Reembolso: [
    {
      titulo: "Migrar processo de reembolso pro app",
      criadaEm: "ago/25",
      concluidaEm: "out/25",
      impacto: "+7pp em Acesso digital",
      sucesso: true,
    },
    {
      titulo: "Reduzir prazo de análise de 30 para 15 dias",
      criadaEm: "mai/25",
      concluidaEm: "jul/25",
      impacto: "+4pp em Agilidade",
      sucesso: true,
    },
  ],
  Aplicativo: [
    {
      titulo: "Refazer fluxo de carteirinha digital",
      criadaEm: "jun/25",
      concluidaEm: "ago/25",
      impacto: "+6pp em App facilitador",
      sucesso: true,
    },
  ],
  "Boas-Vindas": [
    {
      titulo: "Sequência de e-mails educativos nos primeiros 60 dias",
      criadaEm: "mar/25",
      concluidaEm: "mai/25",
      impacto: "+9pp em Comunicação",
      sucesso: true,
    },
  ],
};

// --- Drivers canônicos (alinhados com o backend Eva Engine) --------------
const DRIVERS_CANONICOS = [
  {
    slug: "resolutividade",
    label: "Resolutividade",
    icon: "✅",
    desc: "Resolver no 1º contato",
  },
  {
    slug: "agilidade",
    label: "Agilidade",
    icon: "⚡",
    desc: "Tempo de resposta/espera",
  },
  {
    slug: "atendimento_humano",
    label: "Atendimento humano",
    icon: "🤝",
    desc: "Cordialidade, empatia",
  },
  {
    slug: "acesso_digital",
    label: "Acesso digital",
    icon: "📱",
    desc: "App, portal web",
  },
  {
    slug: "rede_credenciada",
    label: "Rede credenciada",
    icon: "🏥",
    desc: "Médicos, hospitais, labs",
  },
  {
    slug: "comunicacao",
    label: "Comunicação",
    icon: "💬",
    desc: "Clareza de informações",
  },
  {
    slug: "reembolso",
    label: "Reembolso",
    icon: "💰",
    desc: "Processo e prazo",
  },
  {
    slug: "cobertura",
    label: "Cobertura",
    icon: "📋",
    desc: "O que está incluso",
  },
  { slug: "custo", label: "Custo", icon: "💵", desc: "Mensalidade, reajustes" },
  {
    slug: "prevencao",
    label: "Prevenção",
    icon: "🌿",
    desc: "Saúde preventiva",
  },
  {
    slug: "burocracia",
    label: "Burocracia",
    icon: "📎",
    desc: "Processos administrativos",
  },
];
const getDriverInfo = (slug) => DRIVERS_CANONICOS.find((d) => d.slug === slug);

// --- Afinidade etapa ↔ driver -------------------------------------------------
// Mapeia cada etapa da jornada aos drivers (labels do dd.drivers) que mais
// impactam aquela etapa. Peso 1 = central, 0.5 = secundário. Usado pra mostrar
// drivers contextualizados na expansão da etapa (aba Jornada) e no breakdown
// por etapa (aba Drivers).
const ETAPA_DRIVER_AFFINITY = {
  Vendas: [
    { d: "Comunicação proativa", w: 1.0 },
    { d: "Proatividade da equipe", w: 1.0 },
    { d: "Humanização no atendimento", w: 0.7 },
    { d: "Conciliação (Concordância)", w: 0.5 },
  ],
  "Boas-Vindas": [
    { d: "Comunicação proativa", w: 1.0 },
    { d: "App facilitador", w: 0.8 },
    { d: "Humanização no atendimento", w: 0.7 },
    { d: "Proatividade da equipe", w: 0.5 },
  ],
  "Agend. Consultas": [
    { d: "Dificuldade p/ agendar", w: 1.0 },
    { d: "Agilidade no atendimento", w: 0.9 },
    { d: "App facilitador", w: 0.7 },
    { d: "Resolutividade no 1º contato", w: 0.5 },
  ],
  Consulta: [
    { d: "Qualidade médica em consultas", w: 1.0 },
    { d: "Humanização no atendimento", w: 0.8 },
    { d: "Resolutividade no 1º contato", w: 0.6 },
    { d: "Proatividade da equipe", w: 0.4 },
  ],
  "Agend. Exames": [
    { d: "Dificuldade p/ agendar", w: 1.0 },
    { d: "Agilidade no atendimento", w: 0.9 },
    { d: "App facilitador", w: 0.7 },
    { d: "Comunicação proativa", w: 0.4 },
  ],
  Exames: [
    { d: "Agilidade no atendimento", w: 0.9 },
    { d: "Qualidade médica em consultas", w: 0.7 },
    { d: "Comunicação proativa", w: 0.6 },
    { d: "Humanização no atendimento", w: 0.5 },
  ],
  "Atend. Operadora": [
    { d: "Resolutividade no 1º contato", w: 1.0 },
    { d: "Agilidade no atendimento", w: 1.0 },
    { d: "Humanização no atendimento", w: 0.7 },
    { d: "Atend. urgência e emergência", w: 0.6 },
  ],
  Aplicativo: [
    { d: "App facilitador", w: 1.0 },
    { d: "Conciliação (Concordância)", w: 0.7 },
    { d: "Agilidade no atendimento", w: 0.5 },
    { d: "Resolutividade no 1º contato", w: 0.4 },
  ],
  Portal: [
    { d: "App facilitador", w: 0.9 },
    { d: "Conciliação (Concordância)", w: 0.7 },
    { d: "Comunicação proativa", w: 0.5 },
    { d: "Agilidade no atendimento", w: 0.4 },
  ],
  "Promoção da Saúde": [
    { d: "Proatividade da equipe", w: 1.0 },
    { d: "Humanização no atendimento", w: 0.8 },
    { d: "Comunicação proativa", w: 0.7 },
    { d: "Qualidade médica em consultas", w: 0.4 },
  ],
  "Comunicação Op.": [
    { d: "Comunicação proativa", w: 1.0 },
    { d: "Proatividade da equipe", w: 0.7 },
    { d: "Resolutividade no 1º contato", w: 0.5 },
    { d: "App facilitador", w: 0.4 },
  ],
};

// Helper: dado um nome de etapa e a lista base de drivers, retorna a lista
// dos drivers relevantes para aquela etapa, com sentimento e volume escalados
// pelo desempenho da etapa (etapas com score baixo aumentam % negativo).
function getDriversForEtapa(etapaNome, etapaObj, allDrivers) {
  const affinity = ETAPA_DRIVER_AFFINITY[etapaNome];
  if (!affinity || !etapaObj) return [];
  // Score 0-100 → bias -0.4..+0.3 (etapas ruins → mais negativo)
  const bias = (etapaObj.v - 60) / 120;
  return affinity
    .map(({ d: label, w }) => {
      const base = allDrivers.find((b) => b.d === label);
      if (!base) return null;
      const newN = Math.max(1, Math.round((base.n || 0) * w * 0.4)); // proporcional à etapa
      let p = Math.max(0, (base.pos || 0) * (1 + bias));
      let g = Math.max(0, (base.neg || 0) * (1 - bias));
      let u = Math.max(0, base.neu || 0);
      const sum = p + u + g;
      if (sum <= 0)
        return { ...base, n: newN, pos: 0, neu: newN, neg: 0, weight: w };
      p = Math.round((p * newN) / sum);
      u = Math.round((u * newN) / sum);
      g = Math.max(0, newN - p - u);
      return { ...base, n: newN, pos: p, neu: u, neg: g, weight: w };
    })
    .filter(Boolean);
}

// --- 3 Pilares de CX (Sucesso, Esforço, Emoção) ------------------------------
// Cada um dos 11 drivers de NPS pertence a um dos 3 pilares clássicos de CX:
//   • Sucesso  — "você conseguiu o que veio fazer?"
//   • Esforço  — "foi difícil ou trabalhoso?"
//   • Emoção   — "como você se sentiu durante a experiência?"
// Usado pra calcular agregados por pilar e mostrar a classificação ao lado
// do nome do driver.
const DRIVER_TO_PILLAR = {
  "Resolutividade no 1º contato": "Sucesso",
  "Qualidade médica em consultas": "Sucesso",
  "Atend. urgência e emergência": "Sucesso",
  "Conciliação (Concordância)": "Sucesso",
  "Agilidade no atendimento": "Esforço",
  "Dificuldade p/ agendar": "Esforço",
  Reembolso: "Esforço",
  "App facilitador": "Esforço",
  "Humanização no atendimento": "Emoção",
  "Proatividade da equipe": "Emoção",
  "Comunicação proativa": "Emoção",
};
const PILLAR_COLORS = {
  Sucesso: "#16a34a", // verde
  Esforço: "#0284c7", // azul
  Emoção: "#7c3aed", // roxo
};
const getPilarOf = (driverLabel) => DRIVER_TO_PILLAR[driverLabel] || null;

// --- Planos compartilhados no Sistema Unimed (feed cooperativo) ----------
// Em produção viriam de uma API federada com rate limit e moderação por cooperativa.
// Cada plano é publicado pela Unimed de origem e fica visível pras outras filiadas.
const COOP_PLANOS_MOCK = [
  {
    id: "coop_1",
    unimed: "Unimed Vitória",
    uf: "ES",
    avatar: "🌴",
    verif: true,
    postadoEm: "2026-03-28",
    autor: "Fernanda Lima · Gerência de CX",
    titulo: "Paridade funcional Aplicativo–Portal em 90 dias",
    desc: "Identificamos que beneficiários do app tinham CES 18pp acima dos do portal. Refatoramos 12 fluxos do portal para reusar os endpoints do app, incluindo autorização prévia e 2ª via de boleto. Ganho: +14pp em 3 meses.",
    etapa: "Aplicativo",
    drivers: ["acesso_digital", "agilidade", "burocracia"],
    ganhoPp: 14,
    duracaoDias: 90,
    numTarefas: 12,
    responsaveis: ["TI Mobile", "UX Ops"],
    curtidas: 47,
    comentarios: 12,
    reusos: 8,
    tags: ["quick-win", "digital", "retrofit"],
  },
  {
    id: "coop_2",
    unimed: "Unimed Porto Alegre",
    uf: "RS",
    avatar: "🍇",
    verif: true,
    postadoEm: "2026-03-22",
    autor: "Cláudio Weber · Superintendência CX",
    titulo: "Credenciamento emergencial de pediatras — contingência de férias",
    desc: "Padrão histórico: pediatria cai 20pp em Jun-Ago por conta de férias da rede. Montamos acordo com rede 'rotativa' de plantão, comunicamos antecipadamente aos beneficiários, e preparamos agenda de 3 hospitais parceiros. CES não caiu em Jun/25.",
    etapa: "Agend. Consultas",
    drivers: ["rede_credenciada", "agilidade", "comunicacao"],
    ganhoPp: 22,
    duracaoDias: 120,
    numTarefas: 8,
    responsaveis: ["Credenciamento", "Comunicação"],
    curtidas: 89,
    comentarios: 24,
    reusos: 31,
    tags: ["sazonalidade", "rede", "preventivo"],
  },
  {
    id: "coop_3",
    unimed: "Unimed Belo Horizonte",
    uf: "MG",
    avatar: "⛰️",
    verif: true,
    postadoEm: "2026-03-15",
    autor: "Patrícia Andrade · CX + Operações",
    titulo: "SLA de 1h no 1º contato do 0800",
    desc: "Redesenhamos todo o fluxo de atendimento com autonomia de decisão ampliada ao front-line. Treinamento estrutural de 60h por atendente + protocolo de escalonamento claro. CES de Atend. Operadora saiu de 22% para 51% em 6 meses.",
    etapa: "Atend. Operadora",
    drivers: ["resolutividade", "atendimento_humano", "burocracia"],
    ganhoPp: 29,
    duracaoDias: 180,
    numTarefas: 14,
    responsaveis: ["Ops Atendimento", "RH", "CX"],
    curtidas: 142,
    comentarios: 56,
    reusos: 47,
    tags: ["estrutural", "resolutividade", "case de sucesso"],
  },
  {
    id: "coop_4",
    unimed: "Unimed Recife",
    uf: "PE",
    avatar: "🌊",
    verif: false,
    postadoEm: "2026-03-10",
    autor: "Diego Martins · Marketing CX",
    titulo: "Programa de boas-vindas digital (e-mail + WhatsApp)",
    desc: "Sequência automatizada de 60 dias pós-contratação: 5 e-mails educativos + 3 WhatsApp com cartão virtual, app, rede credenciada, canal de dúvidas. CSAT de Boas-Vindas subiu de 72% para 86%.",
    etapa: "Boas-Vindas",
    drivers: ["comunicacao", "acesso_digital"],
    ganhoPp: 14,
    duracaoDias: 60,
    numTarefas: 6,
    responsaveis: ["Marketing", "CX Digital"],
    curtidas: 63,
    comentarios: 18,
    reusos: 22,
    tags: ["onboarding", "automação", "baixo custo"],
  },
  {
    id: "coop_5",
    unimed: "Unimed Curitiba",
    uf: "PR",
    avatar: "🌲",
    verif: true,
    postadoEm: "2026-03-05",
    autor: "Roberto Tanaka · Prevenção & Saúde",
    titulo: "Programa Meu Médico de Confiança",
    desc: "Beneficiários com médico de família têm NPS 22pts acima da base. Lançamos matching automático, piloto com 500 beneficiários, depois expansão para 5mil. CSAT de Consulta subiu +9pp. Case replicável.",
    etapa: "Consulta",
    drivers: ["atendimento_humano", "prevencao", "rede_credenciada"],
    ganhoPp: 9,
    duracaoDias: 150,
    numTarefas: 11,
    responsaveis: ["CX", "Comercial", "Médico"],
    curtidas: 98,
    comentarios: 41,
    reusos: 19,
    tags: ["retenção", "prevenção", "alto impacto"],
  },
  {
    id: "coop_6",
    unimed: "Unimed Fortaleza",
    uf: "CE",
    avatar: "☀️",
    verif: true,
    postadoEm: "2026-02-28",
    autor: "Juliana Sampaio · Reembolso Ops",
    titulo: "Prazo real de reembolso publicado nos canais",
    desc: "Descobrimos que 38% das reclamações de reembolso eram por expectativa desalinhada, não pelo prazo em si. Começamos a publicar prazo médio atualizado semanalmente no app, portal e e-mail de confirmação. CES subiu +11pp sem mudar o prazo real.",
    etapa: "Promoção da Saúde",
    drivers: ["reembolso", "comunicacao"],
    ganhoPp: 11,
    duracaoDias: 45,
    numTarefas: 4,
    responsaveis: ["Reembolso", "Comunicação"],
    curtidas: 76,
    comentarios: 19,
    reusos: 28,
    tags: ["transparência", "quick-win", "comunicação"],
  },
  {
    id: "coop_7",
    unimed: "Unimed Florianópolis",
    uf: "SC",
    avatar: "🏝️",
    verif: false,
    postadoEm: "2026-02-20",
    autor: "Marina Costa · CX Digital",
    titulo: "Triagem digital pré-consulta com IA",
    desc: "Implementamos chatbot de triagem no app que entende sintomas em linguagem natural e direciona para especialidade correta antes do agendamento. Reduziu 42% das consultas desnecessárias com clínico geral.",
    etapa: "Agend. Consultas",
    drivers: ["acesso_digital", "agilidade", "resolutividade"],
    ganhoPp: 18,
    duracaoDias: 120,
    numTarefas: 9,
    responsaveis: ["TI", "Médico", "UX"],
    curtidas: 54,
    comentarios: 22,
    reusos: 11,
    tags: ["ia", "digital", "triagem"],
  },
  {
    id: "coop_8",
    unimed: "Unimed Salvador",
    uf: "BA",
    avatar: "🎭",
    verif: true,
    postadoEm: "2026-02-12",
    autor: "Ana Lúcia Ferreira · Rede Credenciada",
    titulo: "Rede credenciada por geolocalização no app",
    desc: "Adicionamos visualização de mapa com rede credenciada próxima, filtros por especialidade, avaliação e tempo de deslocamento. Beneficiários passaram a escolher rede mais perto de casa, reduzindo queixas de 'rede ruim'.",
    etapa: "Aplicativo",
    drivers: ["rede_credenciada", "acesso_digital"],
    ganhoPp: 7,
    duracaoDias: 75,
    numTarefas: 5,
    responsaveis: ["TI Mobile", "Credenciamento"],
    curtidas: 38,
    comentarios: 14,
    reusos: 9,
    tags: ["ux", "mobile", "rede"],
  },
].map((p, idx) => {
  // Gera 2-3 comentários mock realistas por plano pra demonstração.
  // Em produção, viria de uma API federada com moderação por cooperativa.
  const pool = [
    {
      unimed: "Unimed",
      avatar: "🌱",
      autor: "Renato · Gerência CX",
      texto:
        "Excelente case! Estamos enfrentando algo parecido aqui. Posso trocar uma ideia sobre o processo de implementação?",
    },
    {
      unimed: "Unimed Goiânia",
      avatar: "🌾",
      autor: "Helena Ribeiro · Diretoria CX",
      texto:
        "Replicamos parte desse plano e já vimos resultado. Obrigada por compartilhar!",
    },
    {
      unimed: "Unimed Brasília",
      avatar: "🏛️",
      autor: "Marcos Aurélio · CX + Ops",
      texto:
        "Qual foi o maior desafio na execução? Aqui a rede credenciada é o gargalo.",
    },
    {
      unimed: "Unimed Santos",
      avatar: "⚓",
      autor: "Tânia Oliveira · Marketing CX",
      texto:
        "Interessante a abordagem com drivers. Vamos tentar adaptar pro nosso contexto.",
    },
    {
      unimed: "Unimed Ribeirão",
      avatar: "🌻",
      autor: "Paulo Almeida · Gerência CX",
      texto:
        "Show! Alguém da equipe aí toparia uma call de 30 min pra compartilhar o passo-a-passo?",
    },
    {
      unimed: "Unimed Uberlândia",
      avatar: "🌳",
      autor: "Cristina Souza · Superintendência",
      texto:
        "Parabéns pelo trabalho! O ganho de +pp é expressivo. Como mediram antes e depois?",
    },
  ];
  // 2 comentários se ~até metade, 3 se mais populares
  const n = idx % 2 === 0 ? 3 : 2;
  const start = (idx * 2) % pool.length;
  const coms = Array.from({ length: n }, (_, i) => ({
    ...pool[(start + i) % pool.length],
    id: `cmt_${p.id}_${i}`,
    dt: `2026-04-0${Math.max(1, 5 - i)}`,
  }));
  return { ...p, comentariosLista: coms };
});

// --- Gamificação dos Sucessos do Plano de Ação ---------------------------
const SUCESSO_LEVELS = [
  {
    id: 1,
    name: "Iniciante",
    icon: "🌱",
    min: 0,
    next: 5,
    color: "#9CA3AF",
    bg: "#F3F4F6",
  },
  {
    id: 2,
    name: "Bronze",
    icon: "🥉",
    min: 5,
    next: 15,
    color: "#CD7F32",
    bg: "#FEF3E7",
  },
  {
    id: 3,
    name: "Prata",
    icon: "🥈",
    min: 15,
    next: 30,
    color: "#64748B",
    bg: "#F1F5F9",
  },
  {
    id: 4,
    name: "Ouro",
    icon: "🥇",
    min: 30,
    next: 60,
    color: "#D97706",
    bg: "#FEF3C7",
  },
  {
    id: 5,
    name: "Diamante",
    icon: "💎",
    min: 60,
    next: 120,
    color: "#0891B2",
    bg: "#CFFAFE",
  },
  {
    id: 6,
    name: "Lenda",
    icon: "👑",
    min: 120,
    next: null,
    color: "#9333EA",
    bg: "#F3E8FF",
  },
];
const SUCESSO_BADGES = [
  {
    id: "primeira",
    name: "Primeira vitória",
    icon: "🏆",
    desc: "1ª ação com sucesso registrado",
    check: (s) => s.numSucessos >= 1,
  },
  {
    id: "pp10",
    name: "Marca dos 10",
    icon: "⭐",
    desc: "+10pp cumulados em sucessos",
    check: (s) => s.totalPp >= 10,
  },
  {
    id: "trio",
    name: "Trio impactante",
    icon: "🎯",
    desc: "3 ações com sucesso registrado",
    check: (s) => s.numSucessos >= 3,
  },
  {
    id: "pp25",
    name: "Grande salto",
    icon: "🚀",
    desc: "+25pp cumulados em sucessos",
    check: (s) => s.totalPp >= 25,
  },
  {
    id: "bigwin",
    name: "Virada de jogo",
    icon: "💥",
    desc: "+10pp em uma única ação",
    check: (s) => s.maxSingle >= 10,
  },
  {
    id: "quinteto",
    name: "Quinteto de elite",
    icon: "🔥",
    desc: "5 ações com sucesso registrado",
    check: (s) => s.numSucessos >= 5,
  },
  {
    id: "pp50",
    name: "Meio centena",
    icon: "💫",
    desc: "+50pp cumulados em sucessos",
    check: (s) => s.totalPp >= 50,
  },
  {
    id: "pp100",
    name: "Centurião",
    icon: "👑",
    desc: "+100pp cumulados em sucessos",
    check: (s) => s.totalPp >= 100,
  },
];
// Sanitiza ganhoPp: limita a 200 (qualquer valor maior é claramente bug — NPS vai de -100 a 100, CSAT/CES de 0 a 100)
function safeGanhoPp(g) {
  const n = Number(g);
  if (!isFinite(n) || n < 0) return 0;
  return Math.min(200, n);
}
function getSucessoStats(acoes) {
  const sucessos = acoes.filter((a) => a.sucesso);
  const totalPp = sucessos.reduce(
    (s, a) => s + safeGanhoPp(a.sucesso.ganhoPp),
    0,
  );
  const numSucessos = sucessos.length;
  const maxSingle = sucessos.reduce(
    (m, a) => Math.max(m, safeGanhoPp(a.sucesso.ganhoPp)),
    0,
  );
  const level =
    [...SUCESSO_LEVELS].reverse().find((l) => totalPp >= l.min) ||
    SUCESSO_LEVELS[0];
  const nextLevel = SUCESSO_LEVELS.find((l) => l.id === level.id + 1);
  const progressPct =
    level.next !== null
      ? Math.min(
          100,
          Math.round(((totalPp - level.min) / (level.next - level.min)) * 100),
        )
      : 100;
  const stats = { totalPp, numSucessos, maxSingle };
  const unlockedBadges = SUCESSO_BADGES.filter((b) => b.check(stats));
  return { ...stats, level, nextLevel, progressPct, unlockedBadges };
}

// --- Data ---------------------------------------------------------------------
const ESCALA = [
  {
    min: 80,
    color: "#059669",
    bg: "#ECFDF5",
    label: "✦ Excelente",
    st: "excelente",
  },
  {
    min: 70,
    color: "#16a34a",
    bg: "#F0FDF4",
    label: "✓ Muito Bom",
    st: "muitobom",
  },
  { min: 60, color: "#0284c7", bg: "#F0F9FF", label: "○ Bom", st: "bom" },
  {
    min: 50,
    color: "#d97706",
    bg: "#FFFBEB",
    label: "→ Atenção",
    st: "atencao",
  },
  { min: 40, color: "#dc2626", bg: "#FEF2F2", label: "↓ Ruim", st: "ruim" },
  {
    min: 0,
    color: "#991b1b",
    bg: "#FFF1F2",
    label: "✗ Crítico",
    st: "critico",
  },
];
function etSt(nome, val) {
  const found = ESCALA.find((e) => val >= e.min);
  return found || ESCALA[ESCALA.length - 1];
}
function moe(n) {
  return Math.round(196 * Math.sqrt(0.25 / n)) / 10;
}
function pctChk(arr) {
  return arr.length
    ? Math.round((arr.filter((c) => c.f).length / arr.length) * 100)
    : 0;
}
function diasRest(p) {
  return Math.ceil((new Date(p) - new Date()) / 86400000);
}
function fmtDt(s) {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
}
function todayStr() {
  return new Date().toISOString().split("T")[0];
}

// --- PDF Export ---------------------------------------------------------------
function exportPDF() {
  const style = document.createElement("style");
  style.id = "print-style";
  style.textContent = `@media print{*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}body{background:white!important;}.no-print{display:none!important;}@page{margin:1cm;size:A4;}}`;
  document.head.appendChild(style);
  window.print();
  setTimeout(() => document.head.removeChild(style), 1000);
}

// --- Design Atoms -------------------------------------------------------------
const TT = ({ active, payload, label }) => {
  if (!active || !(payload && payload.length)) return null;
  return (
    <div
      style={{
        background: "rgba(28,28,30,.95)",
        backdropFilter: "blur(12px)",
        borderRadius: 10,
        padding: "8px 12px",
        fontSize: 12,
        boxShadow: "0 8px 24px rgba(0,0,0,.2)",
        border: "1px solid rgba(255,255,255,.1)",
      }}
    >
      <div
        style={{
          color: C.g300,
          fontWeight: 600,
          marginBottom: 4,
          fontSize: 11,
        }}
      >
        {label}
      </div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: "#fff", fontSize: 12 }}>
          <span style={{ color: p.color || C.g300 }}>{p.name}</span>:{" "}
          <b>{p.value}</b>
        </div>
      ))}
    </div>
  );
};

function Tag({ label, color, bg, size = "sm" }) {
  const p = size === "md" ? "4px 10px" : "2px 8px",
    fs = size === "md" ? 12 : 11;
  return (
    <span
      style={{
        background: bg || color + "15",
        color,
        borderRadius: 20,
        padding: p,
        fontSize: fs,
        fontWeight: 600,
        whiteSpace: "nowrap",
        letterSpacing: "-0.01em",
      }}
    >
      {label}
    </span>
  );
}

function Card({ children, style = {}, onClick, hover }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: C.surface,
        borderRadius: 16,
        border: `1px solid ${C.border}`,
        boxShadow:
          hov && hover
            ? "0 8px 32px rgba(0,0,0,.08)"
            : "0 2px 8px rgba(0,0,0,.04)",
        transition: "box-shadow .2s,transform .15s",
        transform: hov && hover ? "translateY(-1px)" : "none",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Section({ title, action, children, pad = true, style = {} }) {
  return (
    <Card style={{ ...style }}>
      {title && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px 0",
            marginBottom: pad ? 14 : 0,
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: C.text,
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </div>
          {action}
        </div>
      )}
      {pad ? (
        <div style={{ padding: title ? "0 20px 18px" : "20px" }}>
          {children}
        </div>
      ) : (
        children
      )}
    </Card>
  );
}

// Opções para cada tipo de período (visual — labels intercambiáveis)
const PER_OPTIONS = {
  mensal: [
    "Abr/26",
    "Mar/26",
    "Fev/26",
    "Jan/26",
    "Dez/25",
    "Nov/25",
    "Out/25",
    "Set/25",
  ],
  trimestral: ["T2/26", "T1/26", "T4/25", "T3/25", "T2/25", "T1/25"],
  semestral: ["S1/26", "S2/25", "S1/25", "S2/24", "S1/24"],
  anual: ["2026", "2025", "2024", "2023", "2022"],
};
const PER_DEFAULT_LABELS = {
  mensal: "Mar/26",
  trimestral: "T1/26",
  semestral: "S2/25",
  anual: "2025",
};

function PeriodPill({ value, onChange }) {
  const [openType, setOpenType] = useState(null);
  const [labels, setLabels] = useState(PER_DEFAULT_LABELS);
  return (
    <div
      style={{
        display: "flex",
        background: C.surf3,
        borderRadius: 10,
        padding: 2,
        gap: 1,
        position: "relative",
      }}
    >
      {Object.keys(PER).map((k) => {
        const isActive = value === k;
        const isOpen = openType === k;
        return (
          <div key={k} style={{ position: "relative" }}>
            <button
              onClick={() => {
                if (isActive) setOpenType(isOpen ? null : k);
                else {
                  onChange(k);
                  setOpenType(null);
                }
              }}
              style={{
                background: isActive ? C.surface : "transparent",
                color: isActive ? C.text : C.text3,
                border: "none",
                borderRadius: 8,
                padding: "5px 9px",
                fontSize: 11,
                fontWeight: isActive ? 600 : 400,
                cursor: "pointer",
                transition: "all .15s",
                boxShadow: isActive ? "0 1px 3px rgba(0,0,0,.1)" : "none",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontFamily: "inherit",
              }}
            >
              {labels[k]}
              <ChevronDown
                size={11}
                style={{ opacity: isActive ? 0.7 : 0.4 }}
              />
            </button>
            {isOpen && (
              <>
                <div
                  onClick={() => setOpenType(null)}
                  style={{ position: "fixed", inset: 0, zIndex: 49 }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    padding: 4,
                    boxShadow: "0 8px 24px rgba(0,0,0,.12)",
                    zIndex: 50,
                    minWidth: 96,
                    maxHeight: 240,
                    overflowY: "auto",
                  }}
                >
                  {PER_OPTIONS[k].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        setLabels((l) => ({ ...l, [k]: opt }));
                        onChange(k);
                        setOpenType(null);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        background:
                          labels[k] === opt ? C.greenBg : "transparent",
                        color: labels[k] === opt ? C.g700 : C.text2,
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 10px",
                        fontSize: 11,
                        fontWeight: labels[k] === opt ? 600 : 400,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// PeriodPillBig — versão prominente para o header com filtro customizado
function PeriodPillBig({ value, onChange, custom, onCustomChange }) {
  const [showCustom, setShowCustom] = useState(false);
  const [openType, setOpenType] = useState(null);
  const [labels, setLabels] = useState(PER_DEFAULT_LABELS);
  const isCustom = value === "custom";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        position: "relative",
      }}
    >
      <Calendar size={15} color={C.text3} />
      <div
        style={{
          fontSize: 11,
          color: C.text4,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginRight: 2,
        }}
      >
        Período
      </div>
      <div
        style={{
          display: "flex",
          background: C.surf3,
          borderRadius: 12,
          padding: 3,
          gap: 2,
          border: `1px solid ${C.border}`,
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        }}
      >
        {Object.keys(PER).map((k) => {
          const isActive = value === k;
          const isOpen = openType === k;
          return (
            <div key={k} style={{ position: "relative" }}>
              <button
                onClick={() => {
                  if (isActive) setOpenType(isOpen ? null : k);
                  else {
                    onChange(k);
                    setOpenType(null);
                    setShowCustom(false);
                  }
                }}
                style={{
                  background: isActive ? C.g700 : "transparent",
                  color: isActive ? "#FFFFFF" : C.text2,
                  border: "none",
                  borderRadius: 9,
                  padding: "7px 12px",
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 500,
                  cursor: "pointer",
                  transition: "all .15s",
                  boxShadow: isActive
                    ? "0 2px 6px rgba(22,101,52,.25)"
                    : "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontFamily: "inherit",
                }}
              >
                {labels[k]}
                <ChevronDown
                  size={13}
                  style={{ opacity: isActive ? 0.85 : 0.5 }}
                />
              </button>
              {isOpen && (
                <>
                  <div
                    onClick={() => setOpenType(null)}
                    style={{ position: "fixed", inset: 0, zIndex: 99 }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 12,
                      padding: 5,
                      boxShadow: "0 12px 32px rgba(0,0,0,.14)",
                      zIndex: 100,
                      minWidth: 120,
                      maxHeight: 280,
                      overflowY: "auto",
                    }}
                  >
                    {PER_OPTIONS[k].map((opt) => (
                      <button
                        key={opt}
                        onClick={() => {
                          setLabels((l) => ({ ...l, [k]: opt }));
                          onChange(k);
                          setOpenType(null);
                          setShowCustom(false);
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          background:
                            labels[k] === opt ? C.greenBg : "transparent",
                          color: labels[k] === opt ? C.g700 : C.text2,
                          border: "none",
                          borderRadius: 8,
                          padding: "8px 12px",
                          fontSize: 12,
                          fontWeight: labels[k] === opt ? 700 : 500,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          whiteSpace: "nowrap",
                          transition: "all .12s",
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
        <button
          onClick={() => {
            setShowCustom((s) => !s);
            setOpenType(null);
          }}
          style={{
            background: isCustom ? C.g700 : "transparent",
            color: isCustom ? "#FFFFFF" : C.text2,
            border: "none",
            borderRadius: 9,
            padding: "7px 14px",
            fontSize: 13,
            fontWeight: isCustom ? 700 : 500,
            cursor: "pointer",
            transition: "all .15s",
            display: "flex",
            alignItems: "center",
            gap: 5,
            boxShadow: isCustom ? "0 2px 6px rgba(22,101,52,.25)" : "none",
            fontFamily: "inherit",
          }}
        >
          <Filter size={12} />{" "}
          {isCustom && custom.inicio
            ? `${custom.inicio} → ${custom.fim || "…"}`
            : "Personalizado"}
        </button>
      </div>
      {showCustom && (
        <div
          style={{
            position: "absolute",
            top: "110%",
            right: 0,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 14,
            boxShadow: "0 12px 32px rgba(0,0,0,.12)",
            zIndex: 200,
            minWidth: 280,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: C.text3,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 8,
            }}
          >
            Filtro personalizado
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 10,
                  color: C.text4,
                  marginBottom: 3,
                  fontWeight: 600,
                }}
              >
                De
              </div>
              <input
                type="date"
                value={custom.inicio}
                onChange={(e) =>
                  onCustomChange({ ...custom, inicio: e.target.value })
                }
                style={{
                  width: "100%",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: "6px 10px",
                  fontSize: 12,
                  fontFamily: "inherit",
                  color: C.text,
                  background: C.bg,
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 10,
                  color: C.text4,
                  marginBottom: 3,
                  fontWeight: 600,
                }}
              >
                Até
              </div>
              <input
                type="date"
                value={custom.fim}
                onChange={(e) =>
                  onCustomChange({ ...custom, fim: e.target.value })
                }
                style={{
                  width: "100%",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: "6px 10px",
                  fontSize: 12,
                  fontFamily: "inherit",
                  color: C.text,
                  background: C.bg,
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button
              onClick={() => {
                onCustomChange({ inicio: "", fim: "" });
                setShowCustom(false);
              }}
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 11,
                fontWeight: 600,
                color: C.text3,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Limpar
            </button>
            <button
              onClick={() => {
                onChange("custom");
                setShowCustom(false);
              }}
              disabled={!custom.inicio || !custom.fim}
              style={{
                background: custom.inicio && custom.fim ? C.g700 : C.surf3,
                color: custom.inicio && custom.fim ? "#FFFFFF" : C.text4,
                border: "none",
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 11,
                fontWeight: 700,
                cursor: custom.inicio && custom.fim ? "pointer" : "not-allowed",
                fontFamily: "inherit",
              }}
            >
              Aplicar filtro
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// PDF Export Modal — escolhe páginas e período antes de gerar
function PDFExportModal({ onClose, onGenerate, currentPeriod }) {
  const ALL_PAGES = [
    { id: "executivo", label: "Visão Geral" },
    { id: "jornada", label: "Jornada" },
    { id: "drivers", label: "Drivers" },
    { id: "tendencias", label: "Tendências" },
    { id: "segmentos", label: "Segmentação" },
    { id: "benchmark", label: "Benchmarking" },
    { id: "acoes", label: "Plano de Ação" },
    { id: "quest", label: "Metodologia" },
  ];
  const [selected, setSelected] = useState(
    ALL_PAGES.reduce((acc, p) => ({ ...acc, [p.id]: true }), {}),
  );
  const [periodo, setPeriodo] = useState(currentPeriod || "trimestral");
  const [customRange, setCustomRange] = useState({ inicio: "", fim: "" });
  const totalSelected = Object.values(selected).filter(Boolean).length;
  function toggleAll(v) {
    setSelected(ALL_PAGES.reduce((acc, p) => ({ ...acc, [p.id]: v }), {}));
  }
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        backdropFilter: "blur(6px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          borderRadius: 18,
          maxWidth: 560,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,.3)",
        }}
      >
        <div
          style={{
            padding: "20px 24px 14px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                background: C.g700,
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Download size={18} color="#FFFFFF" />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.text,
                  letterSpacing: "-0.02em",
                }}
              >
                Exportar PDF
              </h3>
              <div style={{ fontSize: 11, color: C.text4, marginTop: 1 }}>
                Escolha o conteúdo e o período do relatório
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 6,
              borderRadius: 6,
              color: C.text4,
            }}
          >
            <XCircle size={20} />
          </button>
        </div>

        <div style={{ padding: "18px 24px" }}>
          <div
            style={{
              fontSize: 11,
              color: C.text3,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 10,
            }}
          >
            1. Páginas para incluir
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button
              onClick={() => toggleAll(true)}
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: "5px 12px",
                fontSize: 11,
                fontWeight: 600,
                color: C.text2,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Marcar todas
            </button>
            <button
              onClick={() => toggleAll(false)}
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: "5px 12px",
                fontSize: 11,
                fontWeight: 600,
                color: C.text2,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Desmarcar
            </button>
            <div style={{ flex: 1 }} />
            <Tag
              label={`${totalSelected}/${ALL_PAGES.length} selecionadas`}
              color={C.g700}
            />
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}
          >
            {ALL_PAGES.map((p) => (
              <label
                key={p.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 12px",
                  border: `1px solid ${selected[p.id] ? C.g700 : C.border}`,
                  borderRadius: 9,
                  cursor: "pointer",
                  background: selected[p.id] ? C.greenBg : "transparent",
                  transition: "all .15s",
                }}
              >
                <input
                  type="checkbox"
                  checked={selected[p.id]}
                  onChange={(e) =>
                    setSelected({ ...selected, [p.id]: e.target.checked })
                  }
                  style={{
                    accentColor: C.g700,
                    width: 14,
                    height: 14,
                    cursor: "pointer",
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: selected[p.id] ? C.g700 : C.text2,
                  }}
                >
                  {p.label}
                </span>
                {p.id === "acoes" && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.p600,
                      background: C.p100 || "#EDE9FE",
                      padding: "2px 6px",
                      borderRadius: 5,
                      letterSpacing: "0.03em",
                      marginLeft: "auto",
                    }}
                  >
                    SEMPRE ATUAL
                  </span>
                )}
              </label>
            ))}
          </div>

          <div
            style={{
              fontSize: 11,
              color: C.text3,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginTop: 18,
              marginBottom: 6,
            }}
          >
            2. Período do relatório
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 6,
              padding: "8px 12px",
              background: C.p100 || "#EDE9FE",
              border: `1px solid ${C.p400 || "#A78BFA"}40`,
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            <Info
              size={13}
              color={C.p600}
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            <div style={{ fontSize: 10.5, color: C.text2, lineHeight: 1.45 }}>
              O <b>Plano de Ação</b> reflete sempre o estado atual das ações em
              andamento na plataforma — não é segmentado por período. Todas as
              outras páginas usam o período selecionado abaixo.
            </div>
          </div>
          <div
            style={{
              padding: "14px 16px",
              background: C.surf3,
              borderRadius: 10,
              border: `1px solid ${C.border}`,
            }}
          >
            <PeriodPillBig
              value={periodo}
              onChange={setPeriodo}
              custom={customRange}
              onCustomChange={setCustomRange}
            />
            {periodo !== "custom" && PER[periodo] && (
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 12px",
                  background: C.surface,
                  borderRadius: 8,
                  fontSize: 11,
                  color: C.text3,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <ClipboardList size={12} color={C.text4} />
                <span>
                  <b style={{ color: C.text }}>{PER[periodo].label}</b> ·{" "}
                  {PER[periodo].resp.toLocaleString()} respondentes neste
                  período
                </span>
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            padding: "14px 24px 20px",
            borderTop: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: `1px solid ${C.border}`,
              borderRadius: 9,
              padding: "9px 16px",
              fontSize: 12,
              fontWeight: 600,
              color: C.text2,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={() =>
              onGenerate({
                pages: Object.keys(selected).filter((k) => selected[k]),
                periodo,
                customRange,
              })
            }
            disabled={
              totalSelected === 0 ||
              (periodo === "custom" &&
                (!customRange.inicio || !customRange.fim))
            }
            style={{
              background: totalSelected > 0 ? C.g700 : C.surf3,
              color: totalSelected > 0 ? "#FFFFFF" : C.text4,
              border: "none",
              borderRadius: 9,
              padding: "9px 18px",
              fontSize: 12,
              fontWeight: 700,
              cursor: totalSelected > 0 ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
            }}
          >
            <Download size={13} /> Gerar PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// --- NPS Anchor ---------------------------------------------------------------
function NPSAnchor({ d, per, metas }) {
  const prog = Math.min(Math.round((d.nps / metas.nps) * 100), 100);
  const gap = d.nps - metas.nps;
  const up = d.npsVar > 0;

  // Consolidated averages from etapas
  const cesEtapas = d.etapas.filter(
    (e) => (JCFG[e.e] && JCFG[e.e].tipo) === "ces",
  );
  const csatEtapas = d.etapas.filter(
    (e) => (JCFG[e.e] && JCFG[e.e].tipo) === "csat",
  );
  const cesConsolidado = cesEtapas.length
    ? Math.round(cesEtapas.reduce((s, e) => s + e.v, 0) / cesEtapas.length)
    : 0;
  const csatConsolidado = csatEtapas.length
    ? Math.round(csatEtapas.reduce((s, e) => s + e.v, 0) / csatEtapas.length)
    : 0;
  const cesMetaAvg = metas.ces;
  const csatMetaAvg = metas.csat;
  // Sist. Unimed averages (referência) — média dos benchmarks de cada etapa em JCFG
  const cesSysAvg = cesEtapas.length
    ? Math.round(
        cesEtapas.reduce((s, e) => s + ((JCFG[e.e] && JCFG[e.e].sys) || 0), 0) /
          cesEtapas.length,
      )
    : 0;
  const csatSysAvg = csatEtapas.length
    ? Math.round(
        csatEtapas.reduce(
          (s, e) => s + ((JCFG[e.e] && JCFG[e.e].sys) || 0),
          0,
        ) / csatEtapas.length,
      )
    : 0;
  // Jornada CX = média de TODOS os indicadores (CES + CSAT)
  const jornadaCX = d.etapas.length
    ? Math.round(d.etapas.reduce((s, e) => s + e.v, 0) / d.etapas.length)
    : 0;
  const jornadaMeta = metas.jcx;
  const jornadaSysAvg = d.etapas.length
    ? Math.round(
        d.etapas.reduce((s, e) => s + ((JCFG[e.e] && JCFG[e.e].sys) || 0), 0) /
          d.etapas.length,
      )
    : 0;
  return (
    <Card
      style={{
        background: `linear-gradient(135deg,${C.g800} 0%,${C.g700} 100%)`,
        border: "none",
        padding: "20px 24px",
        marginBottom: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          right: -40,
          top: -40,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "rgba(255,255,255,.04)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 60,
          bottom: -30,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(255,255,255,.03)",
        }}
      />
      <div
        style={{
          display: "flex",
          gap: 24,
          alignItems: "center",
          flexWrap: "wrap",
          position: "relative",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              color: "rgba(255,255,255,.5)",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            NPS GERAL
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span
              style={{
                fontSize: 72,
                fontWeight: 700,
                color: "#FFFFFF",
                fontFamily: F.display,
                lineHeight: 1,
                letterSpacing: "-0.04em",
              }}
            >
              {d.nps}
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  color: up ? "#4ade80" : "#f87171",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {Math.abs(d.npsVar)} pts
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.45)" }}>
                vs período anterior
              </div>
            </div>
          </div>
          <div
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,.6)",
              marginTop: 8,
              fontWeight: 500,
            }}
          >
            Meta {metas.nps}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          {/* NPS Scale */}
          {(() => {
            const pct = ((d.nps + 100) / 200) * 100;
            const zoneCurrent =
              d.nps < 0
                ? "Crítico"
                : d.nps < 50
                  ? "Aperfeiçoamento"
                  : d.nps < 75
                    ? "Qualidade"
                    : "Excelência";
            // Zone label positions: left% = (startValue + 100) / 200 * 100
            const zoneLabels = [
              { l: "Crítico", r: "-100 a -1", c: "#f97316", left: 0 },
              { l: "Aperfeiçoamento", r: "0 a 49", c: "#a3e635", left: 50 },
              { l: "Qualidade", r: "50 a 74", c: "#4ade80", left: 75 },
              { l: "Excelência", r: "75 a 100", c: "#166534", left: 87.5 },
            ];
            return (
              <div style={{ marginBottom: 14 }}>
                {/* Bar + pointer */}
                <div style={{ position: "relative", marginBottom: 0 }}>
                  <div
                    style={{
                      height: 10,
                      borderRadius: 5,
                      background:
                        "linear-gradient(to right,#dc2626 0%,#f97316 25%,#fbbf24 40%,#a3e635 50%,#4ade80 62%,#22c55e 75%,#166534 100%)",
                      position: "relative",
                    }}
                  >
                    {/* Pointer circle sits on top of bar */}
                    <div
                      style={{
                        position: "absolute",
                        left: `${pct}%`,
                        top: "50%",
                        transform: "translate(-50%,-50%)",
                        zIndex: 2,
                      }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          background: "#FFFFFF",
                          border: "2.5px solid rgba(0,0,0,.25)",
                          boxShadow: "0 2px 8px rgba(0,0,0,.5)",
                        }}
                      />
                    </div>
                  </div>
                  {/* Zone labels positioned absolutely below bar */}
                  <div
                    style={{ position: "relative", height: 38, marginTop: 2 }}
                  >
                    {/* Tick numbers */}
                    {[
                      [-100, 0],
                      [0, 50],
                      [50, 75],
                      [75, 87.5],
                      [100, 100],
                    ].map(([v, p]) => (
                      <span
                        key={v}
                        style={{
                          position: "absolute",
                          left: `${p}%`,
                          transform: "translateX(-50%)",
                          fontSize: 8,
                          color: "rgba(255,255,255,.35)",
                          top: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {v}
                      </span>
                    ))}
                    {/* Zone labels */}
                    {zoneLabels.map(({ l, r, c, left }) => {
                      const isActive = zoneCurrent === l;
                      return (
                        <div
                          key={l}
                          style={{
                            position: "absolute",
                            left: `${left}%`,
                            top: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            opacity: isActive ? 1 : 0.5,
                            transition: "opacity .3s",
                          }}
                        >
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: c,
                              flexShrink: 0,
                            }}
                          />
                          <div>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: isActive ? 700 : 500,
                                color: isActive
                                  ? "#FFFFFF"
                                  : "rgba(255,255,255,.7)",
                                whiteSpace: "nowrap",
                                lineHeight: 1.2,
                              }}
                            >
                              {l}
                            </div>
                            <div
                              style={{
                                fontSize: 8,
                                color: "rgba(255,255,255,.4)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {r}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Detratores / Neutros / Promotores */}
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                display: "flex",
                height: 8,
                borderRadius: 4,
                overflow: "hidden",
                gap: 1,
              }}
            >
              <div
                title={`Detratores (0-6): ${d.det}%`}
                style={{
                  width: `${d.det}%`,
                  background: "#f87171",
                  flexShrink: 0,
                  transition: "width .8s",
                }}
              />
              <div
                title={`Neutros (7-8): ${d.neut}%`}
                style={{
                  width: `${d.neut}%`,
                  background: "#fbbf24",
                  flexShrink: 0,
                  transition: "width .8s",
                }}
              />
              <div
                title={`Promotores (9-10): ${d.prom}%`}
                style={{
                  width: `${d.prom}%`,
                  background: "#4ade80",
                  flexShrink: 0,
                  transition: "width .8s",
                }}
              />
            </div>
            {/* Labels positioned under each segment */}
            <div style={{ position: "relative", height: 34, marginTop: 2 }}>
              {/* Detratores — starts at left=0 */}
              <div
                style={{
                  position: "absolute",
                  left: "0%",
                  top: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: "#f87171",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#f87171",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    }}
                  >
                    Detratores <b>{d.det}%</b>
                  </div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,.4)" }}>
                    0–6
                  </div>
                </div>
              </div>
              {/* Neutros — starts after det% */}
              <div
                style={{
                  position: "absolute",
                  left: `${d.det}%`,
                  top: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: "#fbbf24",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#fbbf24",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    }}
                  >
                    Neutros <b>{d.neut}%</b>
                  </div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,.4)" }}>
                    7–8
                  </div>
                </div>
              </div>
              {/* Promotores — starts after det+neut% */}
              <div
                style={{
                  position: "absolute",
                  left: `${d.det + d.neut}%`,
                  top: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: "#4ade80",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#4ade80",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    }}
                  >
                    Promotores <b>{d.prom}%</b>
                  </div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,.4)" }}>
                    9–10
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            {[
              ["Respondentes", per.resp.toLocaleString()],
              ["Ondas", per.ondas],
              ["Meta", metas.nps],
              ["Sist. Unimed", SYS_NPS],
            ].map(([l, v]) => (
              <div key={l}>
                <div
                  style={{
                    fontSize: 9,
                    color: "rgba(255,255,255,.4)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {l}
                </div>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 600,
                    color: "#FFFFFF",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* -- Consolidated KPIs -- */}
        <div
          style={{
            display: "flex",
            gap: 10,
            flexShrink: 0,
            flexDirection: "column",
          }}
        >
          {/* Jornada CX destaque */}
          <div
            style={{
              padding: "10px 16px",
              background: "rgba(255,255,255,.14)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,.25)",
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,.15)",
            }}
          >
            <div
              style={{
                fontSize: 9,
                color: "rgba(255,255,255,.55)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 2,
              }}
            >
              JORNADA CX
            </div>
            <div
              style={{
                fontSize: 36,
                fontWeight: 700,
                color: "#FFFFFF",
                letterSpacing: "-0.04em",
                fontFamily: F.display,
                lineHeight: 1,
              }}
            >
              {jornadaCX}%
            </div>
            <div
              style={{
                fontSize: 9,
                color: "rgba(255,255,255,.4)",
                marginTop: 3,
              }}
            >
              {d.etapas.length} etapas · Meta {jornadaMeta}%
            </div>
            <div
              style={{
                fontSize: 9,
                color: "rgba(255,255,255,.55)",
                marginTop: 1,
              }}
            >
              Sist. Unimed{" "}
              <b style={{ color: "rgba(255,255,255,.85)" }}>{jornadaSysAvg}%</b>
            </div>
            <div
              style={{
                marginTop: 5,
                height: 3,
                background: "rgba(255,255,255,.1)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.min(100, Math.round((jornadaCX / jornadaMeta) * 100))}%`,
                  background: "rgba(255,255,255,.7)",
                  borderRadius: 2,
                }}
              />
            </div>
          </div>
          {/* CES e CSAT médios */}
          <div style={{ display: "flex", gap: 8 }}>
            {[
              {
                label: "CES MÉDIO",
                value: cesConsolidado + "%",
                meta: cesMetaAvg,
                sys: cesSysAvg,
                color: "#93c5fd",
                sub: `${cesEtapas.length} etapas`,
              },
              {
                label: "CSAT MÉDIO",
                value: csatConsolidado + "%",
                meta: csatMetaAvg,
                sys: csatSysAvg,
                color: "#86efac",
                sub: `${csatEtapas.length} etapas`,
              },
            ].map((k, i) => (
              <div
                key={i}
                style={{
                  padding: "8px 12px",
                  background: "rgba(255,255,255,.07)",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,.1)",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    color: "rgba(255,255,255,.4)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    marginBottom: 2,
                  }}
                >
                  {k.label}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: k.color,
                    letterSpacing: "-0.03em",
                    fontFamily: F.display,
                    lineHeight: 1,
                  }}
                >
                  {k.value}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: "rgba(255,255,255,.3)",
                    marginTop: 2,
                  }}
                >
                  {k.sub} · Meta {k.meta}%
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: "rgba(255,255,255,.5)",
                    marginTop: 1,
                  }}
                >
                  Sist. Unimed{" "}
                  <b style={{ color: "rgba(255,255,255,.8)" }}>{k.sys}%</b>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

// --- Consolidated trends (computed from ETAPA_HIST averages) -----------------
const CES_COLS = [
  { k: "ef", c: "#059669", l: "Ext.Fácil" },
  { k: "f", c: "#34c759", l: "Fácil" },
  { k: "n", c: "#f59e0b", l: "Normal" },
  { k: "d", c: "#ef4444", l: "Difícil" },
  { k: "ed", c: "#dc2626", l: "Ext.Difícil" },
];
const CSAT_COLS = [
  { k: "ms", c: "#059669", l: "5 — Muito Satisfeito" },
  { k: "s", c: "#34c759", l: "4 — Satisfeito" },
  { k: "r", c: "#f59e0b", l: "3 — Razoável" },
  { k: "i", c: "#ef4444", l: "2 — Insatisfeito" },
  { k: "mi", c: "#dc2626", l: "1 — Muito Insatisfeito" },
];

function DistBar({ dist, tipo, small }) {
  if (!dist) return null;
  const cols = tipo === "ces" ? CES_COLS : CSAT_COLS;
  const topLabel =
    tipo === "ces"
      ? "Ext.Fácil + Fácil = Top2Box%"
      : "Satisfeito + Muito Satisfeito = CSAT%";
  return (
    <div>
      <div
        style={{
          display: "flex",
          height: small ? 6 : 10,
          borderRadius: small ? 3 : 5,
          overflow: "hidden",
          gap: 1,
        }}
      >
        {cols.map((c) => (
          <div
            key={c.k}
            title={`${c.l}: ${dist[c.k]}%`}
            style={{
              width: `${dist[c.k] || 0}%`,
              background: c.c,
              flexShrink: 0,
              transition: "width .5s",
            }}
          />
        ))}
      </div>
      {!small && (
        <React.Fragment>
          <div
            style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}
          >
            {cols.map((c) => (
              <div
                key={c.k}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  fontSize: 10,
                  color: C.text3,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: c.c,
                  }}
                />
                {c.l} <b>{dist[c.k] || 0}%</b>
              </div>
            ))}
          </div>
          <div
            style={{
              fontSize: 10,
              color: C.text4,
              marginTop: 4,
              fontStyle: "italic",
            }}
          >
            {topLabel}
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

// --- Historical data per etapa (6-point monthly trend) ------------------------
const ETAPA_HIST = {
  Vendas: [
    { p: "Out", v: 72 },
    { p: "Nov", v: 73 },
    { p: "Dez", v: 74 },
    { p: "Jan", v: 75 },
    { p: "Fev", v: 76 },
    { p: "Mar", v: 76 },
  ],
  "Boas-Vindas": [
    { p: "Out", v: 74 },
    { p: "Nov", v: 75 },
    { p: "Dez", v: 76 },
    { p: "Jan", v: 78 },
    { p: "Fev", v: 78 },
    { p: "Mar", v: 78 },
  ],
  "Agend. Consultas": [
    { p: "Out", v: 32 },
    { p: "Nov", v: 30 },
    { p: "Dez", v: 28 },
    { p: "Jan", v: 25 },
    { p: "Fev", v: 26 },
    { p: "Mar", v: 28 },
  ],
  Consulta: [
    { p: "Out", v: 79 },
    { p: "Nov", v: 80 },
    { p: "Dez", v: 80 },
    { p: "Jan", v: 82 },
    { p: "Fev", v: 83 },
    { p: "Mar", v: 82 },
  ],
  "Agend. Exames": [
    { p: "Out", v: 36 },
    { p: "Nov", v: 34 },
    { p: "Dez", v: 31 },
    { p: "Jan", v: 29 },
    { p: "Fev", v: 30 },
    { p: "Mar", v: 32 },
  ],
  Exames: [
    { p: "Out", v: 59 },
    { p: "Nov", v: 61 },
    { p: "Dez", v: 62 },
    { p: "Jan", v: 62 },
    { p: "Fev", v: 63 },
    { p: "Mar", v: 63 },
  ],
  "Atend. Operadora": [
    { p: "Out", v: 16 },
    { p: "Nov", v: 16 },
    { p: "Dez", v: 16 },
    { p: "Jan", v: 16 },
    { p: "Fev", v: 17 },
    { p: "Mar", v: 18 },
  ],
  Aplicativo: [
    { p: "Out", v: 58 },
    { p: "Nov", v: 60 },
    { p: "Dez", v: 62 },
    { p: "Jan", v: 62 },
    { p: "Fev", v: 64 },
    { p: "Mar", v: 66 },
  ],
  Portal: [
    { p: "Out", v: 48 },
    { p: "Nov", v: 50 },
    { p: "Dez", v: 51 },
    { p: "Jan", v: 52 },
    { p: "Fev", v: 53 },
    { p: "Mar", v: 54 },
  ],
  "Promoção da Saúde": [
    { p: "Out", v: 70 },
    { p: "Nov", v: 71 },
    { p: "Dez", v: 72 },
    { p: "Jan", v: 73 },
    { p: "Fev", v: 74 },
    { p: "Mar", v: 75 },
  ],
  "Comunicação Op.": [
    { p: "Out", v: 57 },
    { p: "Nov", v: 58 },
    { p: "Dez", v: 59 },
    { p: "Jan", v: 60 },
    { p: "Fev", v: 61 },
    { p: "Mar", v: 62 },
  ],
};

// --- Consolidated trends (computed from ETAPA_HIST averages) -----------------
const CES_ETAPAS_NAMES = [
  "Agend. Consultas",
  "Agend. Exames",
  "Atend. Operadora",
  "Aplicativo",
  "Portal",
];
const CSAT_ETAPAS_NAMES = [
  "Vendas",
  "Boas-Vindas",
  "Consulta",
  "Exames",
  "Promoção da Saúde",
  "Comunicação Op.",
];
const MESES_HIST = ["Out", "Nov", "Dez", "Jan", "Fev", "Mar"];
const CES_TREND = MESES_HIST.map((p, mi) => ({
  p,
  v: Math.round(
    CES_ETAPAS_NAMES.reduce(
      (s, n) =>
        s + (ETAPA_HIST[n] && ETAPA_HIST[n][mi] ? ETAPA_HIST[n][mi].v : 0),
      0,
    ) / CES_ETAPAS_NAMES.length,
  ),
  meta: Math.round(
    CES_ETAPAS_NAMES.reduce(
      (s, n) => s + ((JCFG[n] && JCFG[n].meta) || 65),
      0,
    ) / CES_ETAPAS_NAMES.length,
  ),
  sys: Math.round(
    CES_ETAPAS_NAMES.reduce((s, n) => s + ((JCFG[n] && JCFG[n].sys) || 50), 0) /
      CES_ETAPAS_NAMES.length,
  ),
}));
const CSAT_TREND = MESES_HIST.map((p, mi) => ({
  p,
  v: Math.round(
    CSAT_ETAPAS_NAMES.reduce(
      (s, n) =>
        s + (ETAPA_HIST[n] && ETAPA_HIST[n][mi] ? ETAPA_HIST[n][mi].v : 0),
      0,
    ) / CSAT_ETAPAS_NAMES.length,
  ),
  meta: Math.round(
    CSAT_ETAPAS_NAMES.reduce(
      (s, n) => s + ((JCFG[n] && JCFG[n].meta) || 80),
      0,
    ) / CSAT_ETAPAS_NAMES.length,
  ),
  sys: Math.round(
    CSAT_ETAPAS_NAMES.reduce(
      (s, n) => s + ((JCFG[n] && JCFG[n].sys) || 65),
      0,
    ) / CSAT_ETAPAS_NAMES.length,
  ),
}));

// --- Trends agrupados por granularidade (mês, trimestre, semestre, ano) ------
// Metas e sistema base usados em todas as granularidades
const _META_NPS_TR = 70,
  _SYS_NPS_TR = 52;
const _META_CES_TR = 66,
  _SYS_CES_TR = 48;
const _META_CSAT_TR = 79,
  _SYS_CSAT_TR = 70;
const TREND_BY_AGRUP = {
  mes: {
    label: "Mensal",
    nps: [
      { p: "Abr/25", n: 48, s: 50 },
      { p: "Mai/25", n: 49, s: 50 },
      { p: "Jun/25", n: 51, s: 50 },
      { p: "Jul/25", n: 52, s: 51 },
      { p: "Ago/25", n: 52, s: 51 },
      { p: "Set/25", n: 54, s: 51 },
      { p: "Out/25", n: 54, s: 51 },
      { p: "Nov/25", n: 55, s: 52 },
      { p: "Dez/25", n: 54, s: 52 },
      { p: "Jan/26", n: 57, s: 52 },
      { p: "Fev/26", n: 59, s: 52 },
      { p: "Mar/26", n: 62, s: 52 },
    ],
    ces: [
      { p: "Abr/25", v: 36, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Mai/25", v: 37, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Jun/25", v: 38, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Jul/25", v: 39, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Ago/25", v: 40, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Set/25", v: 40, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Out/25", v: 41, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Nov/25", v: 42, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Dez/25", v: 42, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Jan/26", v: 43, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Fev/26", v: 44, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "Mar/26", v: 45, meta: _META_CES_TR, sys: _SYS_CES_TR },
    ],
    csat: [
      { p: "Abr/25", v: 69, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Mai/25", v: 70, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Jun/25", v: 70, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Jul/25", v: 71, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Ago/25", v: 72, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Set/25", v: 72, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Out/25", v: 73, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Nov/25", v: 73, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Dez/25", v: 74, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Jan/26", v: 74, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Fev/26", v: 75, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "Mar/26", v: 75, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
    ],
  },
  tri: {
    label: "Trimestral",
    nps: [
      { p: "T1/25", n: 50, s: 50 },
      { p: "T2/25", n: 51, s: 51 },
      { p: "T3/25", n: 53, s: 51 },
      { p: "T4/25", n: 55, s: 52 },
      { p: "T1/26", n: 60, s: 52 },
    ],
    ces: [
      { p: "T1/25", v: 36, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "T2/25", v: 38, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "T3/25", v: 40, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "T4/25", v: 42, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "T1/26", v: 44, meta: _META_CES_TR, sys: _SYS_CES_TR },
    ],
    csat: [
      { p: "T1/25", v: 69, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "T2/25", v: 70, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "T3/25", v: 72, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "T4/25", v: 73, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "T1/26", v: 75, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
    ],
  },
  sem: {
    label: "Semestral",
    nps: [
      { p: "S1/24", n: 42, s: 48 },
      { p: "S2/24", n: 46, s: 49 },
      { p: "S1/25", n: 50, s: 50 },
      { p: "S2/25", n: 54, s: 51 },
      { p: "S1/26", n: 60, s: 52 },
    ],
    ces: [
      { p: "S1/24", v: 30, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "S2/24", v: 33, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "S1/25", v: 37, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "S2/25", v: 41, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "S1/26", v: 44, meta: _META_CES_TR, sys: _SYS_CES_TR },
    ],
    csat: [
      { p: "S1/24", v: 64, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "S2/24", v: 67, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "S1/25", v: 70, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "S2/25", v: 73, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "S1/26", v: 75, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
    ],
  },
  ano: {
    label: "Anual",
    nps: [
      { p: "2023", n: 38, s: 46 },
      { p: "2024", n: 44, s: 48 },
      { p: "2025", n: 52, s: 50 },
      { p: "2026", n: 62, s: 52 },
    ],
    ces: [
      { p: "2023", v: 25, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "2024", v: 32, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "2025", v: 39, meta: _META_CES_TR, sys: _SYS_CES_TR },
      { p: "2026", v: 45, meta: _META_CES_TR, sys: _SYS_CES_TR },
    ],
    csat: [
      { p: "2023", v: 60, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "2024", v: 65, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "2025", v: 71, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
      { p: "2026", v: 75, meta: _META_CSAT_TR, sys: _SYS_CSAT_TR },
    ],
  },
};

// --- Etapa Card ---------------------------------------------------------------
function EtCard({ nome, val, vP, dist, distC, onSel, sel }) {
  const cfg = JCFG[nome];
  if (!cfg) return null;
  const st = etSt(nome, val);
  const isCes = cfg.tipo === "ces";
  const delta = val - (vP || val);
  const up = delta >= 0;
  const IconComp = JICONS[nome];
  const hasDist = isCes ? !!dist : !!distC;
  return (
    <div
      onClick={() => onSel(sel ? null : nome)}
      style={{
        background: sel ? st.bg || C.greenBg : C.surface,
        border: `1.5px solid ${sel ? st.color : C.border}`,
        borderRadius: 14,
        padding: "16px 14px 18px",
        cursor: "pointer",
        minWidth: 0,
        flex: "1 1 0",
        boxShadow: sel
          ? `0 0 0 3px ${st.color}20,0 4px 16px rgba(0,0,0,.06)`
          : "0 1px 4px rgba(0,0,0,.04)",
        transition: "all .2s",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Icon + badge */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: isCes ? C.blueBg : C.greenBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {IconComp && (
            <IconComp
              size={17}
              color={isCes ? C.blue : C.g700}
              strokeWidth={1.8}
            />
          )}
        </div>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: isCes ? C.blue : C.g700,
            background: isCes ? C.blueBg : C.greenBg,
            padding: "3px 7px",
            borderRadius: 6,
            letterSpacing: "0.04em",
          }}
        >
          {isCes ? "CES" : "CSAT"}
        </span>
      </div>
      {/* Name */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: C.text3,
          marginBottom: 8,
          letterSpacing: "-0.01em",
          lineHeight: 1.35,
          minHeight: 30,
        }}
      >
        {nome}
      </div>
      {/* Big number */}
      <div
        style={{
          fontSize: 36,
          fontWeight: 700,
          color: st.color,
          letterSpacing: "-0.04em",
          fontFamily: F.display,
          lineHeight: 1,
        }}
      >
        {val}%
      </div>
      <div
        style={{ fontSize: 10, color: C.text4, marginTop: 4, lineHeight: 1.4 }}
      >
        Meta {cfg.meta}%
      </div>
      <div
        style={{
          fontSize: 10,
          color: C.text4,
          marginBottom: 10,
          lineHeight: 1.4,
        }}
      >
        Sist. Unimed {cfg.sys}%
      </div>
      {/* Distribution bar */}
      {hasDist && (
        <div style={{ marginBottom: 10 }}>
          <DistBar dist={isCes ? dist : distC} tipo={cfg.tipo} small />
        </div>
      )}
      {/* Progress bar */}
      <div
        style={{
          height: 5,
          background: C.surf3,
          borderRadius: 3,
          position: "relative",
          marginBottom: 12,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: "absolute",
            left: `${cfg.meta}%`,
            top: 0,
            bottom: 0,
            width: 1.5,
            background: C.p500,
            opacity: 0.7,
            zIndex: 1,
          }}
        />
        <div
          style={{
            height: "100%",
            width: `${Math.min(val, 100)}%`,
            background: st.color,
            borderRadius: 3,
            transition: "width .8s ease",
          }}
        />
      </div>
      {/* Status badge */}
      <div
        style={{
          marginTop: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div>
          <Tag label={st.label} color={st.color} bg={st.bg} />
        </div>
        {vP !== undefined && (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 3,
              color: up ? C.green : C.red,
            }}
          >
            {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
            <span>
              {up ? "+" : ""}
              {delta}pp
            </span>
            <span
              style={{ fontSize: 10, fontWeight: 400, color: C.text4 }}
            ></span>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Eva Insights -------------------------------------------------------------
function EvaInsights({ insights, d, per }) {
  const [open, setOpen] = useState(0); // overview expanded by default
  const tc = {
    overview: { bg: "#F0F9FF", bc: "#0369a1", bl: "Visão Geral", Icon: Brain },
    alerta: { bg: C.redBg, bc: C.red, bl: "Alerta", Icon: CircleAlert },
    oportunidade: { bg: C.greenBg, bc: C.green, bl: "Oportunidade", Icon: Zap },
    padrao: { bg: C.blueBg, bc: C.blue, bl: "Padrão", Icon: ScanSearch },
    predicao: { bg: C.purpleBg, bc: C.purple, bl: "Predição", Icon: Brain },
  };

  // Compute dynamic overview from data
  let overviewItem = null;
  if (d && per) {
    const stages = [...d.etapas].sort((a, b) => b.v - a.v);
    const best = stages[0];
    const worst = stages[stages.length - 1];
    const cesEt = d.etapas.filter(
      (e) => (JCFG[e.e] && JCFG[e.e].tipo) === "ces",
    );
    const csatEt = d.etapas.filter(
      (e) => (JCFG[e.e] && JCFG[e.e].tipo) === "csat",
    );
    const cesAvg = cesEt.length
      ? Math.round(cesEt.reduce((s, e) => s + e.v, 0) / cesEt.length)
      : 0;
    const csatAvg = csatEt.length
      ? Math.round(csatEt.reduce((s, e) => s + e.v, 0) / csatEt.length)
      : 0;
    const drivers = (d.drivers || []).map((dr) => ({
      ...dr,
      netPp: ((dr.pos - dr.neg) / dr.n) * 100,
    }));
    const topPos = drivers.slice().sort((a, b) => b.netPp - a.netPp)[0];
    const topNeg = drivers.slice().sort((a, b) => a.netPp - b.netPp)[0];
    const npsLabel =
      d.nps >= 75
        ? "Excelência"
        : d.nps >= 50
          ? "Qualidade"
          : d.nps >= 0
            ? "Aperfeiçoamento"
            : "Crítico";
    const trendWord =
      d.npsVar > 0 ? "subiu" : d.npsVar < 0 ? "caiu" : "manteve-se estável";
    const trendIcon = d.npsVar > 0 ? "↗" : d.npsVar < 0 ? "↘" : "→";
    overviewItem = {
      tipo: "overview",
      prio: 0,
      titulo: `Análise geral do período — NPS ${d.nps} (${npsLabel}) ${trendIcon}`,
      corpo: `Em ${per.label}, o NPS Geral está em ${d.nps} pontos, classificado como ${npsLabel}, e ${trendWord} ${Math.abs(d.npsVar)} pts vs. período anterior. A base é composta por ${d.prom}% promotores, ${d.neut}% neutros e ${d.det}% detratores, totalizando ${per.resp.toLocaleString()} respondentes. No nível das etapas da jornada, o CSAT médio está em ${csatAvg}% (${csatEt.length} etapas de satisfação) e o CES médio em ${cesAvg}% (${cesEt.length} etapas de esforço). O melhor indicador do período é "${best.e}" com ${best.v}% Top2Box. O pior é "${worst.e}" com apenas ${worst.v}%, sendo a principal alavanca de melhoria do NPS. Entre os ${drivers.length} drivers analisados nos verbatims, "${topPos && topPos.d}" lidera em sentimento positivo (${topPos ? Math.round(topPos.netPp) : 0}pp net) enquanto "${topNeg && topNeg.d}" é o maior atrito declarado (${topNeg ? Math.round(topNeg.netPp) : 0}pp net).`,
      acao: `Priorizar planos de ação para "${worst.e}" e atacar os drivers de atrito em "${topNeg && topNeg.d}". Manter e amplificar os pontos fortes em "${best.e}" e "${topPos && topPos.d}" como casos de sucesso comunicáveis.`,
    };
  }

  const allItems = overviewItem ? [overviewItem, ...insights] : insights;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          background: C.g800,
          borderRadius: 12,
          marginBottom: 4,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: `linear-gradient(135deg,${C.g600},${C.p500})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Sparkles size={16} color="#fff" />
        </div>
        <div>
          <div
            style={{
              color: "#FFFFFF",
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: "-0.02em",
            }}
          >
            Eva — Insights Proativos
          </div>
          <div style={{ color: "rgba(255,255,255,.55)", fontSize: 11 }}>
            {allItems.length} insights gerados automaticamente
          </div>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 4,
            background: "rgba(52,199,89,.15)",
            borderRadius: 20,
            padding: "3px 9px",
          }}
        >
          <div
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: C.green,
            }}
          />
          <span style={{ fontSize: 10, color: C.green, fontWeight: 600 }}>
            Ao vivo
          </span>
        </div>
      </div>
      {allItems.map((ins, i) => {
        const cfg = tc[ins.tipo] || tc.padrao;
        const isO = open === i;
        const { Icon } = cfg;
        return (
          <div
            key={i}
            style={{
              background: cfg.bg,
              borderRadius: 12,
              overflow: "hidden",
              border: `1px solid ${cfg.bc}${ins.tipo === "overview" ? "40" : "20"}`,
              boxShadow:
                ins.tipo === "overview" ? `0 2px 12px ${cfg.bc}15` : "none",
            }}
          >
            <div
              onClick={() => setOpen(isO ? null : i)}
              style={{
                padding: "12px 14px",
                cursor: "pointer",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: cfg.bc + "18",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                <Icon size={14} color={cfg.bc} strokeWidth={2} />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    marginBottom: 4,
                    alignItems: "center",
                  }}
                >
                  <Tag label={cfg.bl} color={cfg.bc} bg={cfg.bc + "18"} />
                  {ins.tipo !== "overview" && (
                    <span style={{ fontSize: 10, color: C.text4 }}>
                      Prioridade {ins.prio}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {ins.titulo}
                </div>
              </div>
              {isO ? (
                <ChevronUp size={15} color={C.text4} />
              ) : (
                <ChevronDown size={15} color={C.text4} />
              )}
            </div>
            {isO && (
              <div style={{ padding: "0 14px 12px 40px" }}>
                <p
                  style={{
                    margin: "0 0 10px",
                    fontSize: 13,
                    color: C.text2,
                    lineHeight: 1.65,
                  }}
                >
                  {ins.corpo}
                </p>
                <div
                  style={{
                    background: "rgba(0,0,0,.04)",
                    borderRadius: 8,
                    padding: "8px 12px",
                    display: "flex",
                    gap: 6,
                  }}
                >
                  <span style={{ fontSize: 12 }}>💡</span>
                  <div>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: cfg.bc,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 1,
                      }}
                    >
                      Ação sugerida
                    </div>
                    <div
                      style={{ fontSize: 12, color: C.text, fontWeight: 500 }}
                    >
                      {ins.acao}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// --- Eva Chat -----------------------------------------------------------------
function EvaChat({ d, periodo, mensagens, onMensagensUpdate }) {
  const per = PER[periodo];
  const SYSTEM = `Você é a Eva, persona sintética da ${UNIMED} pela 121 Insights. ${per.resp} beneficiários reais, ${per.label}. NPS ${d.nps}. Responda em 1ª pessoa como beneficiária. Direto, honesto. Máximo 4 parágrafos. Português brasileiro informal.`;
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    ref.current && ref.current.scrollIntoView({ behavior: "smooth" });
  }, [mensagens]);
  async function send() {
    if (!input.trim() || loading) return;
    const um = { role: "user", content: input.trim() };
    const nm = [...mensagens, um];
    onMensagensUpdate(nm);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 800,
          system: SYSTEM,
          messages: nm.map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json();
      const reply = {
        role: "assistant",
        content:
          (data.content && data.content[0] ? data.content[0].text : null) ||
          "Erro.",
      };
      onMensagensUpdate([...nm, reply]);
    } catch (e) {
      onMensagensUpdate([
        ...nm,
        { role: "assistant", content: "Erro de conexão." },
      ]);
    }
    setLoading(false);
  }
  const sugs = [
    "Por que o atendimento é tão difícil?",
    "O que mais te satisfaz?",
    "Como é agendar?",
    "Você indicaria a Unimed?",
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", height: 400 }}>
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 8,
          paddingRight: 2,
        }}
      >
        {mensagens.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start",
              maxWidth: "88%",
            }}
          >
            <div
              style={{
                background: m.role === "user" ? C.g800 : C.surf3,
                color: m.role === "user" ? "#FFFFFF" : C.text,
                borderRadius:
                  m.role === "user"
                    ? "16px 16px 4px 16px"
                    : "16px 16px 16px 4px",
                padding: "9px 13px",
                fontSize: 13,
                lineHeight: 1.55,
                border:
                  m.role === "assistant" ? `1px solid ${C.border}` : "none",
              }}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: "flex-start" }}>
            <div
              style={{
                background: C.surf3,
                border: `1px solid ${C.border}`,
                borderRadius: "16px 16px 16px 4px",
                padding: "9px 14px",
                fontSize: 13,
                color: C.text4,
              }}
            >
              Eva está pensando...
            </div>
          </div>
        )}
        <div ref={ref} />
      </div>
      {mensagens.length <= 2 && (
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}
        >
          {sugs.map((s, i) => (
            <button
              key={i}
              onClick={() => setInput(s)}
              style={{
                background: C.surf3,
                border: `1px solid ${C.border}`,
                borderRadius: 20,
                padding: "4px 10px",
                fontSize: 11,
                color: C.text2,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Pergunte sobre a experiência dos beneficiários..."
          style={{
            flex: 1,
            border: `1px solid ${C.border}`,
            borderRadius: 24,
            padding: "9px 16px",
            fontSize: 13,
            outline: "none",
            background: C.surf3,
            color: C.text,
            fontFamily: "inherit",
          }}
        />
        <button
          onClick={send}
          disabled={loading || !input.trim()}
          style={{
            background: loading || !input.trim() ? C.border : C.g700,
            color: "#FFFFFF",
            border: "none",
            borderRadius: "50%",
            width: 38,
            height: 38,
            cursor: loading || !input.trim() ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background .2s",
          }}
        >
          <ArrowUpRight size={16} />
        </button>
      </div>
    </div>
  );
}

// --- Eva Chat History Sidebar -------------------------------------------------
// Tempo relativo amigável: agora / há 5 min / há 2h / ontem / há 3 dias / 12/03
function tempoRelativo(ts) {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const dias = Math.floor(h / 24);
  if (dias === 1) return "ontem";
  if (dias < 7) return `há ${dias} dias`;
  const dt = new Date(ts);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}`;
}

function EvaChatHistory({ chats, ativoId, onSelect, onNew, onDelete }) {
  const [hoverId, setHoverId] = useState(null);
  return (
    <div
      style={{
        width: 240,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        background: C.surf3,
        borderRadius: 14,
        border: `1px solid ${C.border}`,
        overflow: "hidden",
        maxHeight: 480,
      }}
    >
      {/* Header com botão Nova conversa */}
      <div
        style={{
          padding: "12px 12px 10px",
          borderBottom: `1px solid ${C.border}`,
          background: C.surface,
        }}
      >
        <button
          onClick={onNew}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            background: C.g700,
            color: "#FFFFFF",
            border: "none",
            borderRadius: 10,
            padding: "9px 12px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
            transition: "all .15s",
            boxShadow: "0 1px 3px rgba(22,101,52,.25)",
          }}
        >
          <Plus size={14} /> Nova conversa
        </button>
      </div>
      {/* Label */}
      <div
        style={{
          padding: "10px 14px 4px",
          fontSize: 9,
          color: C.text4,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        Histórico ({chats.length})
      </div>
      {/* Lista scrollável */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 6px 8px" }}>
        {chats.length === 0 ? (
          <div
            style={{
              padding: "20px 14px",
              fontSize: 11,
              color: C.text4,
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            Nenhuma conversa ainda.
          </div>
        ) : (
          chats.map((c) => {
            const isActive = c.id === ativoId;
            const isHover = hoverId === c.id;
            return (
              <div
                key={c.id}
                onClick={() => onSelect(c.id)}
                onMouseEnter={() => setHoverId(c.id)}
                onMouseLeave={() => setHoverId(null)}
                style={{
                  position: "relative",
                  padding: "9px 11px 9px 12px",
                  marginBottom: 2,
                  borderRadius: 9,
                  cursor: "pointer",
                  background: isActive
                    ? C.greenBg
                    : isHover
                      ? C.surface
                      : "transparent",
                  border: `1px solid ${isActive ? C.g300 : "transparent"}`,
                  transition: "all .12s",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? C.g700 : C.text2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    paddingRight: isHover ? 22 : 0,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.35,
                  }}
                >
                  {c.titulo}
                </div>
                <div style={{ fontSize: 9, color: C.text4, marginTop: 2 }}>
                  {tempoRelativo(c.criadoEm)} · {c.mensagens.length}{" "}
                  {c.mensagens.length === 1 ? "msg" : "msgs"}
                </div>
                {/* Delete on hover */}
                {isHover && chats.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(c.id);
                    }}
                    title="Excluir conversa"
                    style={{
                      position: "absolute",
                      right: 6,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      borderRadius: 5,
                      display: "flex",
                      alignItems: "center",
                      color: C.red,
                      fontFamily: "inherit",
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// --- Action Plan flat list ---------------------------------------------------
const SC = {
  pendente: { label: "Pendente", color: "#FF9500" },
  em_andamento: { label: "Em andamento", color: "#007AFF" },
  concluido: { label: "Concluído", color: "#34C759" },
  pausado: { label: "Pausado", color: "#AEAEB2" },
  arquivado: { label: "Arquivado", color: "#6B7280" },
};
const PC = {
  alta: { label: "Alta", color: "#FF3B30" },
  media: { label: "Média", color: "#FF9500" },
  baixa: { label: "Baixa", color: "#34C759" },
};

// Flat initial actions derived from indicator plans
const ACOES_INIT = [
  {
    id: "at1",
    titulo: "Redesenhar atendimento 0800",
    desc: "Autonomia ampliada e script de resolução no 1º contato.",
    etapa: "Atend. Operadora",
    tipo: "ces",
    v_atual: 18,
    v_meta: 65,
    resp: "Gerência de CX",
    prazo: "2026-06-30",
    prio: "alta",
    status: "em_andamento",
    origem: "Eva — Alerta crítico",
    check: [
      {
        i: 1,
        t: "Diagnosticar causas raiz",
        f: true,
        inicio: "2026-01-10",
        fim: "2026-01-31",
        concluido: "2026-01-28",
      },
      {
        i: 2,
        t: "Workshop equipe",
        f: true,
        inicio: "2026-02-01",
        fim: "2026-02-28",
        concluido: "2026-02-14",
      },
      {
        i: 3,
        t: "Novo script implementado",
        f: false,
        inicio: "2026-03-01",
        fim: "2026-04-30",
        concluido: null,
      },
      {
        i: 4,
        t: "Medir CES pós-implantação",
        f: false,
        inicio: "2026-05-01",
        fim: "2026-06-30",
        concluido: null,
      },
    ],
    hist: [
      { dt: "2026-01-10", tx: "Ação criada" },
      { dt: "2026-02-14", tx: "Workshop concluído com 12 atendentes" },
      { dt: "2026-03-01", tx: "CES subiu de 16% para 18%" },
    ],
    med: [
      { p: "Jan", v: 16 },
      { p: "Fev", v: 16 },
      { p: "Mar", v: 18 },
    ],
    links: [
      {
        id: 1,
        label: "📊 Diagnóstico de causas raiz — 0800 (Google Docs)",
        url: "https://docs.google.com/document/d/1a2b3c4d5e6f7g8h9i0jk/edit",
      },
      {
        id: 2,
        label: "🎥 Gravação do workshop de processo (Google Drive)",
        url: "https://drive.google.com/file/d/1zaxy9w8v7u6t5s4r3q/view",
      },
      {
        id: 3,
        label: "📋 Protocolo de autonomia ampliada v2 (Dropbox)",
        url: "https://www.dropbox.com/scl/fi/abc123def456/Protocolo-Autonomia-v2.pdf",
      },
      {
        id: 4,
        label: "📈 Planilha de baseline CES — Jan-Mar 2026 (Drive)",
        url: "https://docs.google.com/spreadsheets/d/1xyz987wvu654tsr321/edit",
      },
    ],
  },
  {
    id: "ag1",
    titulo: "Credenciar especialistas — região norte",
    desc: "Reduzir espera de 23 dias para menos de 10 dias. Foco em Dermatologia.",
    etapa: "Agend. Consultas",
    tipo: "ces",
    v_atual: 28,
    v_meta: 65,
    resp: "Gestão de Rede",
    prazo: "2026-05-31",
    prio: "alta",
    status: "pendente",
    origem: "Eva — Alerta crítico",
    check: [
      {
        i: 1,
        t: "Mapear médicos disponíveis",
        f: false,
        inicio: "2026-03-01",
        fim: "2026-03-15",
        concluido: null,
      },
      {
        i: 2,
        t: "Negociar credenciamentos",
        f: false,
        inicio: "2026-03-16",
        fim: "2026-04-30",
        concluido: null,
      },
      {
        i: 3,
        t: "Medir CES pós-credenciamento",
        f: false,
        inicio: "2026-05-01",
        fim: "2026-05-31",
        concluido: null,
      },
    ],
    hist: [{ dt: "2026-02-15", tx: "Ação criada — espera média: 23 dias" }],
    med: [
      { p: "Jan", v: 25 },
      { p: "Fev", v: 26 },
      { p: "Mar", v: 28 },
    ],
    links: [
      {
        id: 1,
        label: "📍 Mapa de cobertura atual — Região Norte (Google Sheets)",
        url: "https://docs.google.com/spreadsheets/d/1norte2025credenciados/edit",
      },
      {
        id: 2,
        label: "📄 Contrato-padrão de credenciamento v3 (Dropbox)",
        url: "https://www.dropbox.com/scl/fi/credenc123/Contrato-Credenciamento-v3.docx",
      },
      {
        id: 3,
        label: "📊 Análise de gap de especialidades (Drive)",
        url: "https://docs.google.com/presentation/d/1gap-especialidades-2026/edit",
      },
    ],
  },
  {
    id: "por1",
    titulo: "Paridade funcional Aplicativo–Portal",
    desc: "Replicar funcionalidades do app no portal web: agenda, carteirinha, resultados.",
    etapa: "Portal",
    tipo: "ces",
    v_atual: 54,
    v_meta: 68,
    resp: "TI / Produto",
    prazo: "2026-07-31",
    prio: "media",
    status: "em_andamento",
    origem: "Eva — Oportunidade",
    check: [
      {
        i: 1,
        t: "Levantamento de gaps funcionais",
        f: true,
        inicio: "2026-01-15",
        fim: "2026-02-15",
        concluido: "2026-02-10",
      },
      {
        i: 2,
        t: "Priorizar por impacto CES",
        f: true,
        inicio: "2026-02-16",
        fim: "2026-03-15",
        concluido: "2026-03-05",
      },
      {
        i: 3,
        t: "Desenvolver agenda online",
        f: false,
        inicio: "2026-04-01",
        fim: "2026-06-30",
        concluido: null,
      },
      {
        i: 4,
        t: "Deploy e comunicação",
        f: false,
        inicio: "2026-07-01",
        fim: "2026-07-31",
        concluido: null,
      },
    ],
    hist: [
      { dt: "2026-01-20", tx: "8 gaps funcionais identificados" },
      { dt: "2026-03-05", tx: "Priorização concluída" },
    ],
    med: [
      { p: "Jan", v: 50 },
      { p: "Fev", v: 52 },
      { p: "Mar", v: 54 },
    ],
    links: [
      {
        id: 1,
        label: "🗂️ Matriz de gaps funcionais App vs Portal (Google Sheets)",
        url: "https://docs.google.com/spreadsheets/d/1gaps-app-portal-2026/edit",
      },
      {
        id: 2,
        label: "🎨 Protótipo Figma — nova agenda no portal (Drive)",
        url: "https://drive.google.com/file/d/1figma-agenda-portal/view",
      },
      {
        id: 3,
        label: "📑 Especificação técnica de API shared (Dropbox)",
        url: "https://www.dropbox.com/scl/fi/spec-api/Spec-API-Portal-v1.pdf",
      },
    ],
  },
  {
    id: "ree1",
    titulo: "Comunicar prazo real de reembolso",
    desc: "Mostrar prazo real (22 dias) ao invés de prometer 15 dias e descumprir. Portal e app exibem status em tempo real.",
    etapa: "Atend. Operadora",
    tipo: "ces",
    v_atual: 18,
    v_meta: 65,
    resp: "CX + TI",
    prazo: "2026-05-15",
    prio: "alta",
    status: "em_andamento",
    origem: "Eva — Driver de atrito",
    check: [
      {
        i: 1,
        t: "Auditar prazo médio real (últimos 12 meses)",
        f: true,
        inicio: "2026-02-01",
        fim: "2026-02-15",
        concluido: "2026-02-12",
      },
      {
        i: 2,
        t: "Atualizar prazo nos canais de comunicação",
        f: true,
        inicio: "2026-02-16",
        fim: "2026-03-15",
        concluido: "2026-03-10",
      },
      {
        i: 3,
        t: "Implementar status do reembolso no app",
        f: false,
        inicio: "2026-03-16",
        fim: "2026-05-15",
        concluido: null,
      },
    ],
    hist: [
      { dt: "2026-02-01", tx: "Ação criada" },
      { dt: "2026-02-12", tx: "Prazo médio confirmado: 22 dias" },
      { dt: "2026-03-10", tx: "Comunicação atualizada nos canais oficiais" },
    ],
    med: [
      { p: "Jan", v: 16 },
      { p: "Fev", v: 17 },
      { p: "Mar", v: 18 },
    ],
  },
  {
    id: "csa1",
    titulo: "Programa Meu Médico de Confiança",
    desc: "Beneficiários com médico de família têm NPS 22pts acima da média. Lançar programa de matching e comunicar benefício.",
    etapa: "Consulta",
    tipo: "csat",
    v_atual: 82,
    v_meta: 85,
    resp: "CX + Comercial",
    prazo: "2026-03-31",
    prio: "alta",
    status: "concluido",
    origem: "Eva — Padrão identificado",
    check: [
      {
        i: 1,
        t: "Mapear médicos elegíveis ao programa",
        f: true,
        inicio: "2026-02-15",
        fim: "2026-03-15",
        concluido: "2026-03-12",
      },
      {
        i: 2,
        t: "Definir critérios de matching",
        f: true,
        inicio: "2026-03-01",
        fim: "2026-03-10",
        concluido: "2026-03-08",
      },
      {
        i: 3,
        t: "Lançar piloto com 500 beneficiários",
        f: true,
        inicio: "2026-03-11",
        fim: "2026-03-25",
        concluido: "2026-03-22",
      },
      {
        i: 4,
        t: "Comunicação nas renovações",
        f: true,
        inicio: "2026-03-26",
        fim: "2026-03-31",
        concluido: "2026-03-30",
      },
    ],
    hist: [
      { dt: "2026-02-15", tx: "Ação criada com base em insight Eva" },
      { dt: "2026-03-28", tx: "Piloto lançado com 500 beneficiários" },
      {
        dt: "2026-03-31",
        tx: "Concluída — CSAT de Consulta subiu 12pp nos beneficiários do piloto",
      },
    ],
    med: [
      { p: "Jan", v: 80 },
      { p: "Fev", v: 81 },
      { p: "Mar", v: 82 },
    ],
    sucesso: {
      ganhoPp: 12,
      observacao:
        "Beneficiários com médico de família atribuído ao programa apresentaram CSAT de 94% vs 82% da base geral. 12pp de impacto mensurado no piloto de 500 pessoas.",
      registradoEm: "2026-04-01",
    },
    links: [
      {
        id: 1,
        label: "📋 Critérios de matching médico-beneficiário (Google Docs)",
        url: "https://docs.google.com/document/d/1criterios-matching-medico/edit",
      },
      {
        id: 2,
        label: "📊 Relatório do piloto — 500 beneficiários (Drive)",
        url: "https://docs.google.com/spreadsheets/d/1piloto-meu-medico-500/edit",
      },
      {
        id: 3,
        label: "📈 Apresentação de resultados Q1 (Dropbox)",
        url: "https://www.dropbox.com/scl/fi/q1-results/Apresentacao-MeuMedico-Q1.pptx",
      },
      {
        id: 4,
        label: "🎬 Vídeo-tutorial para beneficiários (Drive)",
        url: "https://drive.google.com/file/d/1video-tutorial-meu-medico/view",
      },
    ],
  },
  {
    id: "bvi1",
    titulo: "Programa de boas-vindas digital — primeiros 60 dias",
    desc: "Sequência de e-mails + WhatsApp educando o novo beneficiário sobre rede, app, canais e benefícios.",
    etapa: "Boas-Vindas",
    tipo: "csat",
    v_atual: 78,
    v_meta: 82,
    resp: "Marketing + CX",
    prazo: "2026-05-30",
    prio: "media",
    status: "pendente",
    origem: "Manual",
    check: [
      {
        i: 1,
        t: "Mapear jornada atual de boas-vindas",
        f: false,
        inicio: "2026-04-01",
        fim: "2026-04-15",
        concluido: null,
      },
      {
        i: 2,
        t: "Criar conteúdos (5 e-mails + 3 WhatsApp)",
        f: false,
        inicio: "2026-04-16",
        fim: "2026-05-10",
        concluido: null,
      },
      {
        i: 3,
        t: "Configurar automação",
        f: false,
        inicio: "2026-05-11",
        fim: "2026-05-25",
        concluido: null,
      },
      {
        i: 4,
        t: "Go-live e medir CSAT",
        f: false,
        inicio: "2026-05-26",
        fim: "2026-05-30",
        concluido: null,
      },
    ],
    hist: [{ dt: "2026-03-20", tx: "Ação criada" }],
    med: [
      { p: "Jan", v: 75 },
      { p: "Fev", v: 78 },
      { p: "Mar", v: 78 },
    ],
  },
  {
    id: "emr1",
    titulo: "Reduzir SLA de triagem em pronto-socorro",
    desc: "Triagem de Manchester aplicada com tempo médio < 8 minutos. Foco nos PSs com pior CES.",
    etapa: "Atend. Operadora",
    tipo: "ces",
    v_atual: 18,
    v_meta: 65,
    resp: "Operações Hospitalares",
    prazo: "2026-08-31",
    prio: "alta",
    status: "pendente",
    origem: "Eva — Alerta crítico",
    check: [
      {
        i: 1,
        t: "Identificar 3 PSs com pior CES",
        f: false,
        inicio: "2026-04-01",
        fim: "2026-04-15",
        concluido: null,
      },
      {
        i: 2,
        t: "Treinar equipe de triagem",
        f: false,
        inicio: "2026-04-16",
        fim: "2026-06-15",
        concluido: null,
      },
      {
        i: 3,
        t: "Implementar painel de tempos em tempo real",
        f: false,
        inicio: "2026-05-01",
        fim: "2026-07-31",
        concluido: null,
      },
      {
        i: 4,
        t: "Medir CES pós-implantação",
        f: false,
        inicio: "2026-08-01",
        fim: "2026-08-31",
        concluido: null,
      },
    ],
    hist: [{ dt: "2026-03-25", tx: "Ação criada após análise da Eva" }],
    med: [
      { p: "Jan", v: 16 },
      { p: "Fev", v: 17 },
      { p: "Mar", v: 18 },
    ],
  },
  {
    id: "psa1",
    titulo: "Campanha de check-up para renovações corporativas",
    desc: "Promoção da Saúde foi o indicador com maior crescimento. Usar como ROI nas renovações B2B.",
    etapa: "Promoção da Saúde",
    tipo: "csat",
    v_atual: 75,
    v_meta: 78,
    resp: "Comercial B2B",
    prazo: "2026-06-30",
    prio: "media",
    status: "em_andamento",
    origem: "Eva — Oportunidade",
    check: [
      {
        i: 1,
        t: "Compilar dados de adesão ao check-up",
        f: true,
        inicio: "2026-02-01",
        fim: "2026-02-28",
        concluido: "2026-02-25",
      },
      {
        i: 2,
        t: "Calcular ROI por empresa",
        f: true,
        inicio: "2026-03-01",
        fim: "2026-03-31",
        concluido: "2026-03-25",
      },
      {
        i: 3,
        t: "Material comercial pronto",
        f: false,
        inicio: "2026-04-01",
        fim: "2026-04-30",
        concluido: null,
      },
      {
        i: 4,
        t: "Aplicar nas renovações Q2",
        f: false,
        inicio: "2026-05-01",
        fim: "2026-06-30",
        concluido: null,
      },
    ],
    hist: [
      { dt: "2026-02-01", tx: "Ação criada" },
      { dt: "2026-02-25", tx: "68% das menções positivas citam check-up" },
      {
        dt: "2026-03-25",
        tx: "ROI calculado: economia média de 18% em sinistralidade",
      },
    ],
    med: [
      { p: "Jan", v: 72 },
      { p: "Fev", v: 73 },
      { p: "Mar", v: 75 },
    ],
  },
  {
    id: "app1",
    titulo: "Otimização da carteirinha digital",
    desc: "Reduzir tempo de carregamento da carteirinha no app de 4s para <1s.",
    etapa: "Aplicativo",
    tipo: "ces",
    v_atual: 66,
    v_meta: 72,
    resp: "TI Mobile",
    prazo: "2026-03-15",
    prio: "baixa",
    status: "concluido",
    origem: "Manual",
    check: [
      {
        i: 1,
        t: "Profiling do código atual",
        f: true,
        inicio: "2026-01-15",
        fim: "2026-01-31",
        concluido: "2026-01-28",
      },
      {
        i: 2,
        t: "Refatorar fetch + cache local",
        f: true,
        inicio: "2026-02-01",
        fim: "2026-02-28",
        concluido: "2026-02-22",
      },
      {
        i: 3,
        t: "Deploy e medir CES",
        f: true,
        inicio: "2026-03-01",
        fim: "2026-03-15",
        concluido: "2026-03-10",
      },
    ],
    hist: [
      { dt: "2026-01-15", tx: "Ação criada" },
      { dt: "2026-02-22", tx: "Tempo médio caiu de 4.2s para 0.8s" },
      { dt: "2026-03-10", tx: "Concluída — CES subiu 4pp no mês" },
    ],
    med: [
      { p: "Jan", v: 62 },
      { p: "Fev", v: 64 },
      { p: "Mar", v: 66 },
    ],
    sucesso: {
      ganhoPp: 4,
      observacao:
        "CES da etapa Aplicativo subiu de 62% para 66% após o deploy. Tempo de carregamento caiu de 4.2s para 0.8s.",
      registradoEm: "2026-03-12",
    },
    links: [
      {
        id: 1,
        label: "Especificação técnica — carteirinha v2",
        url: "https://drive.google.com/file/d/1abc2def3ghi4jkl/view",
      },
      {
        id: 2,
        label: "Relatório de performance pós-deploy",
        url: "https://docs.google.com/document/d/5xyz6wqrs7tuv8",
      },
    ],
  },
].map((a) => ({
  ...a,
  v_baseline: a.v_baseline ?? (a.med && a.med[0] ? a.med[0].v : a.v_atual - 2),
}));

function MiniSparkline({ med, meta, tipo }) {
  if (!med || med.length < 2) return null;
  const vals = med.map((m) => m.v);
  const w = 64,
    h = 26;
  const max = Math.max(...vals, meta) + 2;
  const min = Math.max(0, Math.min(...vals, meta) - 5);
  const x = (i) => Math.round((i / (vals.length - 1)) * w);
  const y = (v) => Math.round(h - ((v - min) / (max - min)) * h);
  const pts = vals.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const my = y(meta);
  const last = vals[vals.length - 1];
  const prev = vals[vals.length - 2];
  const up = last >= prev;
  return (
    <svg width={w} height={h} style={{ overflow: "visible" }}>
      <line
        x1={0}
        y1={my}
        x2={w}
        y2={my}
        stroke={C.p500}
        strokeWidth={1}
        strokeDasharray="3 2"
        opacity={0.5}
      />
      <polyline
        points={pts}
        fill="none"
        stroke={up ? C.green : C.red}
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {vals.map((v, i) => (
        <circle
          key={i}
          cx={x(i)}
          cy={y(v)}
          r={i === vals.length - 1 ? 3 : 2}
          fill={i === vals.length - 1 ? (up ? C.green : C.red) : C.border}
        />
      ))}
    </svg>
  );
}

function AcaoCard({ a, sel, onSel, onEdit, onDel }) {
  const sc = SC[a.status] || SC.pendente;
  const pc = PC[a.prio] || PC.media;
  const pr = pctChk(a.check);
  const d = diasRest(a.prazo);
  const isCes = a.tipo === "ces";
  return (
    <div
      onClick={() => onSel(sel ? null : a.id)}
      style={{
        background: C.surface,
        border: `1.5px solid ${sel ? sc.color : C.border}`,
        borderRadius: 14,
        padding: "14px 16px",
        cursor: "pointer",
        boxShadow: sel
          ? `0 0 0 3px ${sc.color}15,0 4px 16px rgba(0,0,0,.06)`
          : "0 1px 4px rgba(0,0,0,.04)",
        transition: "all .2s",
      }}
    >
      {/* Edit/Delete */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1, paddingRight: 8 }}>
          <div
            style={{
              display: "flex",
              gap: 5,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <Tag label={sc.label} color={sc.color} />
            <Tag label={pc.label} color={pc.color} />
            <Tag label={a.etapa} color={isCes ? C.blue : C.g700} />
            <Tag label={a.resp} color={C.purple || "#7C68EE"} />
            {a.sucesso && (
              <Tag
                label={`🏆 Sucesso +${a.sucesso.ganhoPp}pp`}
                color={C.green}
                bg={C.greenBg}
              />
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: C.text,
              letterSpacing: "-0.01em",
              lineHeight: 1.4,
            }}
          >
            {a.titulo}
          </div>
        </div>
        <div
          style={{ display: "flex", gap: 4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onEdit(a)}
            style={{
              background: "none",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: C.text3,
            }}
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={() => onDel(a.id)}
            style={{
              background: "none",
              border: `1px solid ${C.red}30`,
              borderRadius: 8,
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: C.red,
            }}
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
      {/* Progress */}
      <div style={{ marginBottom: 8 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 10,
            color: C.text4,
            marginBottom: 3,
          }}
        >
          <span>
            {a.check.filter((c) => c.f).length}/{a.check.length} tarefas
          </span>
          <span
            style={{ fontWeight: 600, color: pr >= 100 ? C.green : C.text3 }}
          >
            {pr}%
          </span>
        </div>
        <div
          style={{
            height: 4,
            background: C.surf3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pr}%`,
              background: pr >= 100 ? C.green : C.g700,
              borderRadius: 2,
              transition: "width .5s",
            }}
          />
        </div>
      </div>
      {/* Bottom row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <MiniSparkline med={a.med} meta={a.v_meta} tipo={a.tipo} />
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: isCes ? C.blue : C.g700,
            }}
          >
            {isCes ? "CES" : "CSAT"}: <b>{a.v_atual}%</b> → <b>{a.v_meta}%</b>
          </div>
          <div
            style={{
              fontSize: 10,
              color: d < 0 ? C.red : d < 15 ? C.orange : C.text4,
              fontWeight: d < 0 || d < 15 ? 600 : 400,
            }}
          >
            {d < 0 ? `${Math.abs(d)}d atrasada` : `${d}d restantes`} ·{" "}
            {fmtDt(a.prazo)}
          </div>
        </div>
      </div>
    </div>
  );
}

function AcaoDetalhe({ a, onClose, onUpdate, onShare, onPublishWizard }) {
  const [check, setCheck] = useState(a.check);
  const [status, setStatus] = useState(a.status);
  const [novaObs, setNovaObs] = useState("");
  const [hist, setHist] = useState(a.hist);
  // Edição inline de datas do checklist: valor = "<taskId>:<campo>" (campo = inicio | fim | concluido)
  const [editingDate, setEditingDate] = useState(null);
  function updateTaskDate(taskId, field, value) {
    const u = check.map((c) =>
      c.i === taskId ? { ...c, [field]: value || null } : c,
    );
    setCheck(u);
    onUpdate({ ...a, check: u });
    setEditingDate(null);
  }
  // ─── Sucesso (registrado quando a ação é concluída) ─────────────────────
  const [sucessoForm, setSucessoForm] = useState(false);
  const [ganhoInput, setGanhoInput] = useState("");
  const [obsInput, setObsInput] = useState("");
  // Abre o form de sucesso já pré-preenchendo o ganho com a evolução (atual - baseline)
  function abrirFormSucesso() {
    const delta = (Number(a.v_atual) || 0) - (Number(a.v_baseline) || 0);
    setGanhoInput(String(Math.max(0, Math.round(delta * 10) / 10)));
    setObsInput("");
    setSucessoForm(true);
  }
  function salvarSucesso(andPublish = false) {
    const ganho = parseFloat(ganhoInput);
    if (isNaN(ganho) || ganho < 0) return;
    const novoSucesso = {
      ganhoPp: ganho,
      observacao: obsInput.trim(),
      registradoEm: todayStr(),
    };
    const acaoComSucesso = { ...a, sucesso: novoSucesso };
    onUpdate(acaoComSucesso);
    setSucessoForm(false);
    setGanhoInput("");
    setObsInput("");
    // Se o usuário escolheu "Registrar + Publicar", abre o wizard de publicação
    if (andPublish && onPublishWizard) {
      onPublishWizard(acaoComSucesso);
    }
  }
  function removerSucesso() {
    if (!confirm("Remover o sucesso registrado desta ação?")) return;
    onUpdate({ ...a, sucesso: null });
  }
  const sc = SC[status] || SC.pendente;
  const pr = pctChk(check);
  function tog(id) {
    const today = todayStr();
    const u = check.map((c) =>
      c.i === id ? { ...c, f: !c.f, concluido: !c.f ? today : null } : c,
    );
    setCheck(u);
    onUpdate({ ...a, check: u });
  }
  function addObs() {
    if (!novaObs.trim()) return;
    const u = [...hist, { dt: todayStr(), tx: novaObs.trim() }];
    setHist(u);
    onUpdate({ ...a, hist: u });
    setNovaObs("");
  }
  function chgSt(s) {
    setStatus(s);
    onUpdate({ ...a, status: s });
  }
  // ─── Adicionar links inline direto no AcaoDetalhe ──────────────────────
  const [novoLinkLabel, setNovoLinkLabel] = useState("");
  const [novoLinkUrl, setNovoLinkUrl] = useState("");
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  function addLinkInline() {
    if (!novoLinkUrl.trim()) return;
    const url = novoLinkUrl.trim();
    const label =
      novoLinkLabel.trim() || url.replace(/^https?:\/\//, "").slice(0, 40);
    const novoLink = { id: Date.now(), label, url };
    const listaAtual = a.links || [];
    onUpdate({ ...a, links: [...listaAtual, novoLink] });
    setNovoLinkLabel("");
    setNovoLinkUrl("");
    setLinkFormOpen(false);
  }
  function removeLinkInline(linkId) {
    onUpdate({ ...a, links: (a.links || []).filter((l) => l.id !== linkId) });
  }
  const isCes = a.tipo === "ces";
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: "20px 22px",
        boxShadow: "0 4px 20px rgba(0,0,0,.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <Tag label={a.etapa} color={isCes ? C.blue : C.g700} />
            <Tag
              label={isCes ? "CES" : "CSAT"}
              color={isCes ? C.blue : C.g700}
            />
            {a.origem && <Tag label={a.origem} color={C.p500} />}
          </div>
          <h3
            style={{
              margin: "0 0 4px",
              fontSize: 16,
              fontWeight: 700,
              color: C.text,
              letterSpacing: "-0.02em",
            }}
          >
            {a.titulo}
          </h3>
          <p
            style={{
              margin: "0 0 6px",
              fontSize: 12,
              color: C.text3,
              lineHeight: 1.5,
            }}
          >
            {a.desc}
          </p>
          {/* Counter de dias em aberto (desde criação até hoje ou até conclusão) */}
          {(() => {
            const histOrdenado = (a.hist || [])
              .filter((h) => h.dt)
              .map((h) => h.dt)
              .sort();
            const dtCriacao = histOrdenado[0] || null;
            if (!dtCriacao) return null;
            const dtFim =
              a.status === "concluido" && a.sucesso?.registradoEm
                ? a.sucesso.registradoEm
                : todayStr();
            const diasAbertos = Math.max(
              0,
              Math.floor(
                (new Date(dtFim) - new Date(dtCriacao)) / (1000 * 60 * 60 * 24),
              ),
            );
            const isConcluido = a.status === "concluido";
            const iconBg = isConcluido
              ? C.greenBg
              : diasAbertos > 90
                ? C.orangeBg || "#FFFBEB"
                : C.blueBg;
            const iconColor = isConcluido
              ? C.green
              : diasAbertos > 90
                ? C.orange || "#d97706"
                : C.blue;
            const label = isConcluido
              ? `Plano ativo por ${diasAbertos} dia${diasAbertos === 1 ? "" : "s"} (criado em ${fmtDt(dtCriacao)})`
              : `${diasAbertos} dia${diasAbertos === 1 ? "" : "s"} em aberto desde a criação (${fmtDt(dtCriacao)})`;
            return (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 10,
                  fontWeight: 600,
                  color: iconColor,
                  background: iconBg,
                  border: `1px solid ${iconColor}30`,
                  padding: "3px 8px",
                  borderRadius: 20,
                }}
              >
                <Clock size={10} /> {label}
              </div>
            );
          })()}
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: C.text4,
            marginLeft: 12,
            display: "flex",
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Banner ARQUIVADA — visível só quando status === arquivado */}
      {status === "arquivado" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "#F3F4F6",
            border: `1px solid #D1D5DB`,
            borderRadius: 12,
            padding: "10px 14px",
            marginBottom: 14,
          }}
        >
          <Archive size={16} color="#6B7280" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
              Ação arquivada
            </div>
            <div style={{ fontSize: 10, color: "#6B7280", marginTop: 1 }}>
              Esta ação não aparece no filtro padrão. Selecione o status
              "Arquivado" no filtro pra encontrá-la.
            </div>
          </div>
          <button
            onClick={() => chgSt("concluido")}
            style={{
              background: "#FFFFFF",
              border: `1px solid #D1D5DB`,
              borderRadius: 8,
              padding: "6px 12px",
              fontSize: 11,
              fontWeight: 600,
              color: "#374151",
              cursor: "pointer",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Archive size={12} /> Desarquivar
          </button>
        </div>
      )}

      {/* Bloco SUCESSO — só aparece quando ação está concluída */}
      {status === "concluido" && (
        <div style={{ marginBottom: 16 }}>
          {a.sucesso ? (
            // Sucesso registrado: card destaque
            <div
              style={{
                background: `linear-gradient(135deg,${C.greenBg} 0%,#ECFDF5 100%)`,
                border: `1.5px solid ${C.green}`,
                borderRadius: 14,
                padding: "14px 18px",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: C.green,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 2px 6px rgba(22,163,74,.25)",
                }}
              >
                <Trophy size={22} color="#FFFFFF" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.green,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Sucesso registrado
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: C.text,
                    letterSpacing: "-0.02em",
                    marginTop: 1,
                  }}
                >
                  +{a.sucesso.ganhoPp} pp em {a.etapa}
                </div>
                {a.sucesso.observacao && (
                  <div
                    style={{
                      fontSize: 11,
                      color: C.text3,
                      marginTop: 3,
                      lineHeight: 1.4,
                    }}
                  >
                    {a.sucesso.observacao}
                  </div>
                )}
                <div style={{ fontSize: 9, color: C.text4, marginTop: 4 }}>
                  Registrado em {fmtDt(a.sucesso.registradoEm)}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={() =>
                    onPublishWizard ? onPublishWizard(a) : onShare && onShare(a)
                  }
                  title="Publicar este plano no CooperAção Unimed (outras Unimeds poderão ver e reusar)"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    background: C.purple || "#7C68EE",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 8,
                    padding: "7px 12px",
                    cursor: "pointer",
                    fontSize: 10,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                    boxShadow: `0 2px 8px ${C.purple || "#7C68EE"}30`,
                  }}
                >
                  <Share2 size={12} /> Publicar CooperAção
                </button>
                <button
                  onClick={removerSucesso}
                  title="Remover sucesso registrado"
                  style={{
                    background: "transparent",
                    border: `1px solid ${C.border}`,
                    cursor: "pointer",
                    color: C.text4,
                    padding: "5px",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "inherit",
                  }}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ) : sucessoForm ? (
            // Form aberto
            <div
              style={{
                background: C.greenBg,
                border: `1.5px solid ${C.green}`,
                borderRadius: 14,
                padding: "14px 18px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <Trophy size={16} color={C.green} />
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.green,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Registrar sucesso desta ação
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 140px" }}>
                  <label
                    style={{
                      fontSize: 10,
                      color: C.text3,
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 3,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Quanto melhorou (pp)
                  </label>
                  <input
                    type="number"
                    autoFocus
                    value={ganhoInput}
                    onChange={(e) => setGanhoInput(e.target.value)}
                    placeholder="ex: 5"
                    min="0"
                    step="0.5"
                    style={{
                      width: "100%",
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      padding: "9px 12px",
                      fontSize: 16,
                      fontWeight: 700,
                      color: C.green,
                      outline: "none",
                      background: C.surface,
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{ flex: "3 1 200px" }}>
                  <label
                    style={{
                      fontSize: 10,
                      color: C.text3,
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 3,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Observação (opcional)
                  </label>
                  <input
                    value={obsInput}
                    onChange={(e) => setObsInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && salvarSucesso()}
                    placeholder="Como sabe que funcionou? Qual foi o resultado?"
                    style={{
                      width: "100%",
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      padding: "9px 12px",
                      fontSize: 12,
                      color: C.text,
                      outline: "none",
                      background: C.surface,
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 12,
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => {
                    setSucessoForm(false);
                    setGanhoInput("");
                    setObsInput("");
                  }}
                  style={{
                    background: "transparent",
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: "7px 14px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.text3,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => salvarSucesso(false)}
                  disabled={!ganhoInput || isNaN(parseFloat(ganhoInput))}
                  style={{
                    background:
                      !ganhoInput || isNaN(parseFloat(ganhoInput))
                        ? C.border
                        : C.green,
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 8,
                    padding: "7px 14px",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor:
                      !ganhoInput || isNaN(parseFloat(ganhoInput))
                        ? "default"
                        : "pointer",
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <Trophy size={12} /> Registrar sucesso
                </button>
                <button
                  onClick={() => salvarSucesso(true)}
                  disabled={!ganhoInput || isNaN(parseFloat(ganhoInput))}
                  title="Registrar sucesso e abrir publicação no CooperAção Unimed"
                  style={{
                    background:
                      !ganhoInput || isNaN(parseFloat(ganhoInput))
                        ? C.border
                        : C.purple || "#7C68EE",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 8,
                    padding: "7px 14px",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor:
                      !ganhoInput || isNaN(parseFloat(ganhoInput))
                        ? "default"
                        : "pointer",
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    boxShadow:
                      !ganhoInput || isNaN(parseFloat(ganhoInput))
                        ? "none"
                        : `0 2px 8px ${C.purple || "#7C68EE"}40`,
                  }}
                >
                  <Share2 size={12} /> Registrar + Publicar CooperAção
                </button>
              </div>
            </div>
          ) : (
            // Botão pra abrir o form
            <button
              onClick={abrirFormSucesso}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: C.greenBg,
                color: C.green,
                border: `1.5px dashed ${C.green}`,
                borderRadius: 14,
                padding: "14px 18px",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "inherit",
                transition: "all .15s",
              }}
            >
              <Trophy size={16} /> Registrar sucesso desta ação
            </button>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {/* Left column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Evolução desde a criação do plano (baseline → atual) */}
          {a.v_baseline !== undefined &&
            a.v_baseline !== null &&
            (() => {
              const evolucao = Number(a.v_atual) - Number(a.v_baseline);
              const isPos = evolucao > 0;
              const isNeg = evolucao < 0;
              const cor = isPos ? C.green : isNeg ? C.red : C.text4;
              const bg = isPos
                ? C.greenBg
                : isNeg
                  ? C.redBg || "#FEE2E2"
                  : C.surf3;
              const unidade = a.etapa === "NPS Geral" ? "pts" : "pp";
              return (
                <div
                  style={{
                    background: bg,
                    borderRadius: 10,
                    padding: "10px 14px",
                    border: `1px solid ${cor}30`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: C.text4,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      marginBottom: 5,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <TrendingUp size={10} /> Evolução desde a criação do plano
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{ fontSize: 13, color: C.text3, fontWeight: 500 }}
                    >
                      <b style={{ color: C.text2 }}>
                        {a.v_baseline}
                        {a.etapa === "NPS Geral" ? "" : "%"}
                      </b>{" "}
                      <span style={{ fontSize: 11 }}>ao criar</span>
                    </div>
                    <div style={{ fontSize: 14, color: C.text4 }}>→</div>
                    <div
                      style={{ fontSize: 13, color: C.text3, fontWeight: 500 }}
                    >
                      <b style={{ color: C.text }}>
                        {a.v_atual}
                        {a.etapa === "NPS Geral" ? "" : "%"}
                      </b>{" "}
                      <span style={{ fontSize: 11 }}>agora</span>
                    </div>
                    <div
                      style={{
                        marginLeft: "auto",
                        fontSize: 15,
                        fontWeight: 700,
                        color: cor,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {isPos ? "+" : ""}
                      {evolucao} {unidade}
                    </div>
                  </div>
                </div>
              );
            })()}
          {/* KPIs */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            {[
              [
                "Atual",
                `${a.v_atual}%`,
                (SC[a.status] && SC[a.status].color) || C.text,
              ],
              ["Meta", `${a.v_meta}%`, C.p500],
              [
                "Gap",
                `${Math.abs(a.v_meta - a.v_atual)}pp`,
                a.v_atual >= a.v_meta ? C.green : C.orange,
              ],
              [
                "Prazo",
                fmtDt(a.prazo),
                diasRest(a.prazo) < 0 ? C.red : C.text3,
              ],
            ].map(([l, v, c]) => (
              <div
                key={l}
                style={{
                  background: C.surf3,
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: C.text4,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    marginBottom: 2,
                  }}
                >
                  {l}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: c,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
          {/* Status */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.text4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 6,
              }}
            >
              Status
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {Object.entries(SC)
                .filter(
                  ([k]) =>
                    k !== "arquivado" ||
                    status === "concluido" ||
                    status === "arquivado",
                )
                .map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => chgSt(k)}
                    style={{
                      background: status === k ? v.color : C.surf3,
                      color: status === k ? "#FFFFFF" : C.text3,
                      border: `1px solid ${status === k ? v.color : C.border}`,
                      borderRadius: 20,
                      padding: "4px 11px",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 500,
                      transition: "all .15s",
                    }}
                  >
                    {v.label}
                  </button>
                ))}
            </div>
          </div>
          {/* Sparkline */}
          {a.med && a.med.length > 1 && (
            <div
              style={{
                background: C.surf3,
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: C.text4,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Evolução
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                {a.med.map((m, i) => (
                  <div key={i} style={{ textAlign: "center" }}>
                    <div
                      style={{ fontSize: 14, fontWeight: 700, color: C.g700 }}
                    >
                      {m.v}%
                    </div>
                    <div style={{ fontSize: 10, color: C.text4 }}>{m.p}</div>
                  </div>
                ))}
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.p500 }}>
                    {a.v_meta}%
                  </div>
                  <div style={{ fontSize: 10, color: C.p500 }}>Meta</div>
                </div>
              </div>
            </div>
          )}
          {/* History */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.text4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <History size={10} /> Histórico de atualizações
            </div>
            <div
              style={{
                maxHeight: 160,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 0,
              }}
            >
              {hist.map((h, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 8,
                    padding: "6px 0",
                    borderBottom:
                      i < hist.length - 1 ? `1px solid ${C.surf3}` : "none",
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: C.g700,
                      marginTop: 6,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: C.text4,
                          fontWeight: 600,
                        }}
                      >
                        {fmtDt(h.dt)}
                        {h.hora ? ` · ${h.hora}` : ""}
                      </div>
                      {h.user && (
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: C.purple || "#7C68EE",
                            background: C.purpleBg || "#F0EDFF",
                            padding: "1px 6px",
                            borderRadius: 20,
                          }}
                        >
                          {h.user}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.text,
                        lineHeight: 1.4,
                        marginTop: 1,
                      }}
                    >
                      {h.tx}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <input
                value={novaObs}
                onChange={(e) => setNovaObs(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addObs()}
                placeholder="Adicionar observação..."
                style={{
                  flex: 1,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: "6px 10px",
                  fontSize: 11,
                  outline: "none",
                  background: C.surf3,
                  fontFamily: "inherit",
                  color: C.text,
                }}
              />
              <button
                onClick={addObs}
                style={{
                  background: C.g700,
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Plus size={13} />
              </button>
            </div>
          </div>
          {/* Drivers afetados (read-only) */}
          {a.drivers && a.drivers.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: C.text4,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <Crosshair size={10} /> Drivers afetados
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {a.drivers.map((slug) => {
                  const drv = getDriverInfo(slug);
                  if (!drv) return null;
                  return (
                    <div
                      key={slug}
                      title={drv.desc}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "5px 10px",
                        borderRadius: 20,
                        background: C.purpleBg || "#F0EDFF",
                        border: `1px solid ${C.purple || "#7C68EE"}40`,
                        fontSize: 10,
                        fontWeight: 600,
                        color: C.purple || "#7C68EE",
                      }}
                    >
                      <span style={{ fontSize: 12 }}>{drv.icon}</span>
                      {drv.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Links e arquivos (Drive, Dropbox, etc) — sempre visível, com input inline */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.text4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 5,
                justifyContent: "space-between",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <LinkIcon size={10} /> Links e arquivos{" "}
                {(a.links || []).length > 0 && (
                  <span style={{ color: C.text3 }}>
                    · {(a.links || []).length}
                  </span>
                )}
              </span>
              {!linkFormOpen && (
                <button
                  onClick={() => setLinkFormOpen(true)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 9,
                    fontWeight: 700,
                    color: C.blue,
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    padding: 0,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  <Plus size={10} /> Adicionar
                </button>
              )}
            </div>
            {/* Lista de links existentes */}
            {(a.links || []).length === 0 && !linkFormOpen && (
              <div
                style={{
                  fontSize: 10,
                  color: C.text4,
                  fontStyle: "italic",
                  padding: "8px 0",
                }}
              >
                Nenhum link adicionado. Clique em <b>+ Adicionar</b> para anexar
                documentos, especificações ou relatórios.
              </div>
            )}
            {(a.links || []).length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  marginBottom: linkFormOpen ? 8 : 0,
                }}
              >
                {(a.links || []).map((l) => (
                  <div
                    key={l.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: C.blueBg,
                      border: `1px solid ${C.blue}30`,
                      transition: "all .15s",
                    }}
                  >
                    <LinkIcon
                      size={12}
                      color={C.blue}
                      style={{ flexShrink: 0 }}
                    />
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textDecoration: "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: C.text,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {l.label}
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          color: C.text4,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {l.url}
                      </div>
                    </a>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir em nova aba"
                      style={{ display: "flex", flexShrink: 0 }}
                    >
                      <ExternalLink
                        size={11}
                        color={C.blue}
                        style={{ opacity: 0.7 }}
                      />
                    </a>
                    <button
                      onClick={() => removeLinkInline(l.id)}
                      title="Remover link"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: C.red,
                        padding: 0,
                        display: "flex",
                        flexShrink: 0,
                      }}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Form inline de adicionar link */}
            {linkFormOpen && (
              <div
                style={{
                  background: C.surf3,
                  border: `1.5px solid ${C.blue}40`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <input
                  autoFocus
                  value={novoLinkLabel}
                  onChange={(e) => setNovoLinkLabel(e.target.value)}
                  placeholder="Nome (opcional — ex: Especificação técnica)"
                  style={{
                    width: "100%",
                    border: `1px solid ${C.border}`,
                    borderRadius: 7,
                    padding: "6px 10px",
                    fontSize: 11,
                    outline: "none",
                    background: C.surface,
                    fontFamily: "inherit",
                    color: C.text,
                    boxSizing: "border-box",
                  }}
                />
                <input
                  value={novoLinkUrl}
                  onChange={(e) => setNovoLinkUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addLinkInline()}
                  placeholder="https://drive.google.com/..."
                  style={{
                    width: "100%",
                    border: `1px solid ${C.border}`,
                    borderRadius: 7,
                    padding: "6px 10px",
                    fontSize: 11,
                    outline: "none",
                    background: C.surface,
                    fontFamily: "inherit",
                    color: C.text,
                    boxSizing: "border-box",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => {
                      setLinkFormOpen(false);
                      setNovoLinkLabel("");
                      setNovoLinkUrl("");
                    }}
                    style={{
                      background: "transparent",
                      border: `1px solid ${C.border}`,
                      borderRadius: 7,
                      padding: "5px 10px",
                      fontSize: 10,
                      fontWeight: 600,
                      color: C.text3,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={addLinkInline}
                    disabled={!novoLinkUrl.trim()}
                    style={{
                      background: novoLinkUrl.trim() ? C.blue : C.border,
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 7,
                      padding: "5px 12px",
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: novoLinkUrl.trim() ? "pointer" : "default",
                      fontFamily: "inherit",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Plus size={10} /> Adicionar link
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Right column — checklist */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.text4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Checklist — {pr}%
            </div>
            <span style={{ fontSize: 11, color: C.g700, fontWeight: 600 }}>
              {check.filter((c) => c.f).length}/{check.length}
            </span>
          </div>
          <div
            style={{
              height: 4,
              background: C.surf3,
              borderRadius: 2,
              overflow: "hidden",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${pr}%`,
                background: C.g700,
                borderRadius: 2,
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {check.map((c) => (
              <div
                key={c.i}
                onClick={() => tog(c.i)}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "8px 10px",
                  borderRadius: 10,
                  cursor: "pointer",
                  background: c.f ? C.greenBg : C.surf3,
                  border: `1px solid ${c.f ? C.green + "30" : C.border}`,
                  transition: "all .15s",
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    flexShrink: 0,
                    background: c.f ? C.green : C.surface,
                    border: `1.5px solid ${c.f ? C.green : C.border2}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: 1,
                  }}
                >
                  {c.f && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      color: c.f ? C.text3 : C.text,
                      textDecoration: c.f ? "line-through" : "none",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {c.t}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      marginTop: 3,
                      flexWrap: "wrap",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Início */}
                    {editingDate === `${c.i}:inicio` ? (
                      <input
                        type="date"
                        autoFocus
                        value={c.inicio || ""}
                        onChange={(e) =>
                          updateTaskDate(c.i, "inicio", e.target.value)
                        }
                        onBlur={() => setEditingDate(null)}
                        style={{
                          fontSize: 10,
                          padding: "2px 4px",
                          border: `1px solid ${C.g700}`,
                          borderRadius: 4,
                          outline: "none",
                          fontFamily: "inherit",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          fontSize: 10,
                          color: C.text4,
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <Calendar size={9} />
                        Início: {c.inicio ? fmtDt(c.inicio) : "—"}
                        <Pencil
                          size={9}
                          color={C.text4}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            marginLeft: 2,
                          }}
                          onClick={() => setEditingDate(`${c.i}:inicio`)}
                        />
                      </span>
                    )}
                    {/* Fim */}
                    {editingDate === `${c.i}:fim` ? (
                      <input
                        type="date"
                        autoFocus
                        value={c.fim || ""}
                        onChange={(e) =>
                          updateTaskDate(c.i, "fim", e.target.value)
                        }
                        onBlur={() => setEditingDate(null)}
                        style={{
                          fontSize: 10,
                          padding: "2px 4px",
                          border: `1px solid ${C.g700}`,
                          borderRadius: 4,
                          outline: "none",
                          fontFamily: "inherit",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          fontSize: 10,
                          color: C.text4,
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <Clock size={9} />
                        Fim: {c.fim ? fmtDt(c.fim) : "—"}
                        <Pencil
                          size={9}
                          color={C.text4}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            marginLeft: 2,
                          }}
                          onClick={() => setEditingDate(`${c.i}:fim`)}
                        />
                      </span>
                    )}
                    {/* Concluído (só aparece se tarefa está marcada) */}
                    {c.f &&
                      (editingDate === `${c.i}:concluido` ? (
                        <input
                          type="date"
                          autoFocus
                          value={c.concluido || ""}
                          onChange={(e) =>
                            updateTaskDate(c.i, "concluido", e.target.value)
                          }
                          onBlur={() => setEditingDate(null)}
                          style={{
                            fontSize: 10,
                            padding: "2px 4px",
                            border: `1px solid ${C.green}`,
                            borderRadius: 4,
                            outline: "none",
                            fontFamily: "inherit",
                            color: C.green,
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            fontSize: 10,
                            color: C.green,
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            fontWeight: 600,
                          }}
                        >
                          <CheckCircle2 size={9} />
                          Concluído:{" "}
                          {c.concluido ? fmtDt(c.concluido) : todayStr()}
                          <Pencil
                            size={9}
                            color={C.green}
                            style={{
                              cursor: "pointer",
                              opacity: 0.7,
                              marginLeft: 2,
                            }}
                            onClick={() => setEditingDate(`${c.i}:concluido`)}
                          />
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AcaoModal({ acao, prefilled, onSave, onClose }) {
  // Helper: retorna o valor do último mês (Março 2026) pra uma etapa/indicador
  function getValorUltimoMes(etapa, tipo) {
    const mes = DATA.mensal;
    if (etapa === "NPS Geral") return mes.nps;
    if (etapa === "CSAT Geral") {
      const csatEt = mes.etapas.filter(
        (e) => (JCFG[e.e] && JCFG[e.e].tipo) === "csat",
      );
      return csatEt.length
        ? Math.round(csatEt.reduce((s, e) => s + e.v, 0) / csatEt.length)
        : 0;
    }
    if (etapa === "CES Geral") {
      const cesEt = mes.etapas.filter(
        (e) => (JCFG[e.e] && JCFG[e.e].tipo) === "ces",
      );
      return cesEt.length
        ? Math.round(cesEt.reduce((s, e) => s + e.v, 0) / cesEt.length)
        : 0;
    }
    const et = mes.etapas.find((e) => e.e === etapa);
    return et ? et.v : 0;
  }
  const empty = {
    id: Date.now().toString(),
    titulo: "",
    desc: "",
    etapa: "NPS Geral",
    tipo: "nps",
    v_atual: DATA.mensal?.nps,
    v_baseline: DATA.mensal?.nps,
    v_meta: "",
    resp: "",
    prazo: "",
    prio: "alta",
    status: "pendente",
    check: [],
    hist: [],
    med: [],
    origem: "Manual",
    links: [],
    drivers: [],
  };
  const initial = acao
    ? {
        ...acao,
        links: acao.links || [],
        drivers: acao.drivers || [],
        hist: acao.hist || [],
        v_baseline: acao.v_baseline ?? acao.v_atual,
      }
    : prefilled
      ? (() => {
          // Pre-fill: ajusta etapa e tipo, recalcula v_atual e v_baseline conforme a etapa
          const merged = { ...empty, ...prefilled };
          const valor = getValorUltimoMes(merged.etapa, merged.tipo);
          return { ...merged, v_atual: valor, v_baseline: valor };
        })()
      : empty;
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [newTask, setNewTask] = useState({ t: "", inicio: "", fim: "" });
  const [newLink, setNewLink] = useState({ label: "", url: "" });
  const valid = f.titulo && f.resp && f.prazo && f.v_meta !== "";
  const inpSt = {
    width: "100%",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: "8px 11px",
    fontSize: 13,
    outline: "none",
    background: C.surf3,
    fontFamily: "inherit",
    color: C.text,
    boxSizing: "border-box",
  };
  const lbl = (t) => (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: C.text3,
        marginBottom: 4,
        letterSpacing: "-0.01em",
      }}
    >
      {t}
    </div>
  );
  function addTask() {
    if (!newTask.t.trim()) return;
    setF((p) => ({
      ...p,
      check: [
        ...p.check,
        {
          i: Date.now(),
          t: newTask.t.trim(),
          f: false,
          inicio: newTask.inicio || null,
          fim: newTask.fim || null,
          concluido: null,
        },
      ],
    }));
    setNewTask({ t: "", inicio: "", fim: "" });
  }
  function addLink() {
    if (!newLink.url.trim()) return;
    const url = newLink.url.trim();
    const label =
      newLink.label.trim() || url.replace(/^https?:\/\//, "").slice(0, 40);
    setF((p) => ({
      ...p,
      links: [...(p.links || []), { id: Date.now(), label, url }],
    }));
    setNewLink({ label: "", url: "" });
  }
  function removeLink(id) {
    setF((p) => ({ ...p, links: (p.links || []).filter((l) => l.id !== id) }));
  }
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.4)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: C.surface,
          borderRadius: 20,
          padding: "24px 26px",
          width: "100%",
          maxWidth: 540,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 32px 64px rgba(0,0,0,.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: C.text,
              letterSpacing: "-0.02em",
            }}
          >
            {acao ? "Editar ação" : "Nova ação"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: C.text4,
              display: "flex",
            }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            {lbl("Título")}
            <input
              value={f.titulo}
              onChange={(e) => set("titulo", e.target.value)}
              style={inpSt}
            />
          </div>
          <div>
            {lbl("Descrição")}
            <textarea
              value={f.desc}
              onChange={(e) => set("desc", e.target.value)}
              rows={2}
              style={{ ...inpSt, resize: "vertical" }}
            />
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
          >
            <div style={{ gridColumn: "1 / -1" }}>
              {lbl("Etapa / Indicador")}
              <select
                value={`${f.etapa}|${f.tipo}`}
                onChange={(e) => {
                  const [etapa, tipo] = e.target.value.split("|");
                  // Ao trocar de etapa, pré-preenche v_atual com o valor do último mês
                  // Se for uma ação NOVA (sem acao), também atualiza v_baseline (valor ao criar o plano)
                  const novoAtual = getValorUltimoMes(etapa, tipo);
                  setF((p) => ({
                    ...p,
                    etapa,
                    tipo,
                    v_atual: novoAtual,
                    v_baseline: acao ? p.v_baseline : novoAtual,
                  }));
                }}
                style={inpSt}
              >
                <option value="NPS Geral|nps">NPS Geral</option>
                <option value="CSAT Geral|csat">CSAT Geral</option>
                <option value="CES Geral|ces">CES Geral</option>
                <option disabled>──────── Etapas da Jornada ────────</option>
                {Object.entries(JCFG).map(([etapa, cfg]) => (
                  <option key={etapa} value={`${etapa}|${cfg.tipo}`}>
                    {etapa} ({cfg.label})
                  </option>
                ))}
              </select>
            </div>
            <div>
              {lbl(`Valor atual${f.etapa === "NPS Geral" ? "" : " (%)"}`)}
              <input
                type="number"
                value={f.v_atual}
                onChange={(e) => set("v_atual", e.target.value)}
                style={inpSt}
              />
              <div
                style={{
                  fontSize: 10,
                  color: C.text4,
                  marginTop: 3,
                  fontStyle: "italic",
                }}
              >
                {acao
                  ? `Baseline ao criar: ${f.v_baseline}${f.etapa === "NPS Geral" ? "" : "%"}`
                  : "Pré-preenchido com resultado de Mar/26"}
              </div>
            </div>
            <div>
              {lbl(`Meta${f.etapa === "NPS Geral" ? "" : " (%)"}`)}
              <input
                type="number"
                value={f.v_meta}
                onChange={(e) => set("v_meta", e.target.value)}
                style={inpSt}
              />
            </div>
            <div>
              {lbl("Responsável")}
              <input
                value={f.resp}
                onChange={(e) => set("resp", e.target.value)}
                style={inpSt}
              />
            </div>
            <div>
              {lbl("Prazo")}
              <input
                type="date"
                value={f.prazo}
                onChange={(e) => set("prazo", e.target.value)}
                style={inpSt}
              />
            </div>
            <div>
              {lbl("Prioridade")}
              <select
                value={f.prio}
                onChange={(e) => set("prio", e.target.value)}
                style={inpSt}
              >
                <option value="alta">Alta</option>
                <option value="media">Média</option>
                <option value="baixa">Baixa</option>
              </select>
            </div>
            <div>
              {lbl("Status")}
              <select
                value={f.status}
                onChange={(e) => set("status", e.target.value)}
                style={inpSt}
              >
                <option value="pendente">Pendente</option>
                <option value="em_andamento">Em andamento</option>
                <option value="concluido">Concluído</option>
                <option value="pausado">Pausado</option>
              </select>
            </div>
          </div>
          <div>
            {lbl("Tarefas")}
            {f.check.map((c) => (
              <div
                key={c.i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: C.surf3,
                  marginBottom: 4,
                  fontSize: 12,
                }}
              >
                <span style={{ flex: 1 }}>{c.t}</span>
                <span style={{ color: C.text4, fontSize: 10 }}>
                  {c.inicio ? fmtDt(c.inicio) : ""}
                  {c.fim ? ` → ${fmtDt(c.fim)}` : ""}
                </span>
                <button
                  onClick={() =>
                    setF((p) => ({
                      ...p,
                      check: p.check.filter((x) => x.i !== c.i),
                    }))
                  }
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: C.red,
                    display: "flex",
                  }}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr auto",
                gap: 5,
                marginTop: 4,
              }}
            >
              <input
                placeholder="Nova tarefa..."
                value={newTask.t}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, t: e.target.value }))
                }
                onKeyDown={(e) => e.key === "Enter" && addTask()}
                style={{ ...inpSt, padding: "6px 10px" }}
              />
              <input
                type="date"
                value={newTask.inicio}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, inicio: e.target.value }))
                }
                style={{ ...inpSt, padding: "6px 8px", fontSize: 11 }}
                title="Início"
              />
              <input
                type="date"
                value={newTask.fim}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, fim: e.target.value }))
                }
                style={{ ...inpSt, padding: "6px 8px", fontSize: 11 }}
                title="Fim"
              />
              <button
                onClick={addTask}
                style={{
                  background: C.g700,
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: 8,
                  padding: "0 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Plus size={13} />
              </button>
            </div>
          </div>
          {/* Drivers afetados — multi-select */}
          <div>
            {lbl("Drivers afetados por este plano")}
            <div
              style={{
                fontSize: 10,
                color: C.text4,
                marginTop: -2,
                marginBottom: 6,
              }}
            >
              Selecione quais drivers você espera impactar ao executar este
              plano. Isso ajuda a Eva a correlacionar sucessos e sugerir planos
              semelhantes.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {DRIVERS_CANONICOS.map((drv) => {
                const checked = (f.drivers || []).includes(drv.slug);
                return (
                  <div
                    key={drv.slug}
                    onClick={() => {
                      const curr = f.drivers || [];
                      const next = checked
                        ? curr.filter((x) => x !== drv.slug)
                        : [...curr, drv.slug];
                      setF((p) => ({ ...p, drivers: next }));
                    }}
                    title={drv.desc}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "5px 10px",
                      borderRadius: 20,
                      background: checked ? C.purpleBg || "#F0EDFF" : C.surf3,
                      border: `1px solid ${checked ? C.purple || "#7C68EE" : C.border}`,
                      cursor: "pointer",
                      transition: "all .15s",
                      fontSize: 10,
                      fontWeight: 600,
                      color: checked ? C.purple || "#7C68EE" : C.text3,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        filter: checked ? "none" : "grayscale(60%)",
                        opacity: checked ? 1 : 0.7,
                      }}
                    >
                      {drv.icon}
                    </span>
                    <span>{drv.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Links do plano de ação (Drive, Dropbox, docs externos) */}
          <div>
            {lbl("Links e arquivos (Drive, Dropbox, etc)")}
            {(f.links || []).map((l) => (
              <div
                key={l.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: C.surf3,
                  marginBottom: 4,
                  fontSize: 12,
                }}
              >
                <LinkIcon size={12} color={C.blue} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <div
                    style={{
                      fontWeight: 600,
                      color: C.text,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {l.label}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: C.text4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {l.url}
                  </div>
                </div>
                <button
                  onClick={() => removeLink(l.id)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: C.red,
                    display: "flex",
                    flexShrink: 0,
                  }}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr auto",
                gap: 5,
                marginTop: 4,
              }}
            >
              <input
                placeholder="Nome (opcional)"
                value={newLink.label}
                onChange={(e) =>
                  setNewLink((p) => ({ ...p, label: e.target.value }))
                }
                style={{ ...inpSt, padding: "6px 10px", fontSize: 11 }}
              />
              <input
                placeholder="https://drive.google.com/..."
                value={newLink.url}
                onChange={(e) =>
                  setNewLink((p) => ({ ...p, url: e.target.value }))
                }
                onKeyDown={(e) => e.key === "Enter" && addLink()}
                style={{ ...inpSt, padding: "6px 10px", fontSize: 11 }}
              />
              <button
                onClick={addLink}
                style={{
                  background: C.blue,
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: 8,
                  padding: "0 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Plus size={13} />
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              background: C.surf3,
              color: C.text3,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: "10px 0",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              if (valid) {
                const today = todayStr();
                const agora = new Date().toTimeString().slice(0, 5);
                const user = "Renato"; // em produção viria do auth context
                const isNew = !acao;
                // Detecta mudanças pro histórico
                let histEntry;
                if (isNew) {
                  histEntry = {
                    dt: today,
                    hora: agora,
                    user,
                    tx: "Plano de ação criado",
                  };
                } else {
                  const changes = [];
                  if (acao.titulo !== f.titulo) changes.push("título");
                  if (acao.desc !== f.desc) changes.push("descrição");
                  if (String(acao.v_meta) !== String(f.v_meta))
                    changes.push(`meta → ${f.v_meta}`);
                  if (acao.prazo !== f.prazo)
                    changes.push(`prazo → ${fmtDt(f.prazo)}`);
                  if (acao.resp !== f.resp)
                    changes.push(`responsável → ${f.resp}`);
                  if (acao.prio !== f.prio)
                    changes.push(`prioridade → ${f.prio}`);
                  if (acao.status !== f.status)
                    changes.push(`status → ${f.status}`);
                  if ((acao.check || []).length !== (f.check || []).length)
                    changes.push(`tarefas (${(f.check || []).length})`);
                  if ((acao.drivers || []).length !== (f.drivers || []).length)
                    changes.push(`drivers (${(f.drivers || []).length})`);
                  if ((acao.links || []).length !== (f.links || []).length)
                    changes.push(`links (${(f.links || []).length})`);
                  const tx = changes.length
                    ? `Editou: ${changes.join(", ")}`
                    : "Plano atualizado";
                  histEntry = { dt: today, hora: agora, user, tx };
                }
                const histAtual = f.hist && f.hist.length ? f.hist : [];
                onSave({
                  ...f,
                  id: f.id || today,
                  v_atual: Number(f.v_atual) || 0,
                  v_baseline: Number(f.v_baseline) || Number(f.v_atual) || 0,
                  v_meta: Number(f.v_meta),
                  hist: [...histAtual, histEntry],
                  med: f.med.length
                    ? f.med
                    : [{ p: "Atual", v: Number(f.v_atual) || 0 }],
                  links: f.links || [],
                  drivers: f.drivers || [],
                });
                onClose();
              }
            }}
            disabled={!valid}
            style={{
              flex: 2,
              background: valid ? C.g700 : C.border,
              color: "#FFFFFF",
              border: "none",
              borderRadius: 12,
              padding: "10px 0",
              cursor: valid ? "pointer" : "default",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {acao ? "Salvar" : "Criar ação"}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Zonas de refer?ncia ------------------------------------------------------
const Z_CX = [
  [0, 40, "#99111108"], // Cr?tico
  [40, 50, "#dc262608"], // Ruim
  [50, 60, "#d9770608"], // Aten??o
  [60, 70, "#0284c708"], // Bom
  [70, 80, "#16a34a08"], // Muito Bom
  [80, 100, "#05966908"], // Excelente
];
function Zonas({ tipo }) {
  const zones =
    tipo === "nps"
      ? [
          [0, 30, "#99111108"],
          [30, 50, "#dc262608"],
          [50, 70, "#d9770608"],
          [70, 100, "#05966908"],
        ]
      : Z_CX;
  return (
    <React.Fragment>
      {zones.map(([y1, y2, fill], i) => (
        <ReferenceArea key={i} y1={y1} y2={y2} fill={fill} strokeWidth={0} />
      ))}
    </React.Fragment>
  );
}

// --- App ----------------------------------------------------------------------

export {
  ACOES_INIT,
  AcaoCard,
  AcaoDetalhe,
  AcaoModal,
  C,
  CES_COLS,
  CES_ETAPAS_NAMES,
  CES_TREND,
  CIDADES_COBERTURA,
  COOP_PLANOS_MOCK,
  CSAT_COLS,
  CSAT_ETAPAS_NAMES,
  CSAT_TREND,
  Card,
  DRIVERS_CANONICOS,
  DRIVER_TO_PILLAR,
  DistBar,
  ESCALA,
  ETAPA_DRIVER_AFFINITY,
  ETAPA_HIST,
  EtCard,
  EvaChat,
  EvaChatHistory,
  EvaInsights,
  F,
  HIST_ACOES_POR_ETAPA,
  JCFG,
  JICONS,
  LOGO,
  MESES_HIST,
  META_NPS,
  MUDANCAS_DESDE_ULTIMA_VISITA,
  MiniSparkline,
  NPSAnchor,
  PC,
  PDFExportModal,
  PER,
  PER_DEFAULT_LABELS,
  PER_OPTIONS,
  PILLAR_COLORS,
  PeriodPill,
  PeriodPillBig,
  SC,
  SHORT,
  SUCESSO_BADGES,
  SUCESSO_LEVELS,
  SYS_NPS,
  Section,
  TREND_BY_AGRUP,
  TT,
  Tag,
  ULTIMA_VISITA,
  UNIMED,
  VERBATINS_POR_ETAPA,
  Z_CX,
  Zonas,
  _META_CES_TR,
  _META_CSAT_TR,
  _META_NPS_TR,
  _SYS_CES_TR,
  _SYS_CSAT_TR,
  _SYS_NPS_TR,
  diasRest,
  etSt,
  exportPDF,
  fmtDt,
  getDriverInfo,
  getDriversForEtapa,
  getPilarOf,
  getSucessoStats,
  moe,
  pctChk,
  safeGanhoPp,
  tempoRelativo,
  todayStr,
};
